/**
 * Customer Support Module Page
 * Comprehensive support platform with tickets, AI chat, CSAT, surveys, forums, and more
 */

import React, { useState, useEffect, useCallback } from 'react';
import { config } from '@/src/config';
import {
  MessageSquare,
  Book,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  Search,
  Filter,
  TrendingUp,
  Award,
  HelpCircle,
  Headphones,
  BarChart3,
  X,
  Loader2,
  Edit,
  Eye,
  Trash2,
  Send,
  Bot,
  Star,
  Settings,
  UserCheck,
  UserPlus,
  MessageCircle,
  FileQuestion,
  Compass,
  Activity,
  Zap,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  ChevronRight,
  User,
  Mail,
  Phone,
  Video,
  ExternalLink,
  PlayCircle,
  Monitor,
  Paperclip,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/ui/PhoneInput';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useAuth } from '@/contexts/AuthContext';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import { apiClient } from '@/services/apiClient';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';
import OnboardingTab from './components/OnboardingTab';
import { MarkdownHelpSection } from '@/src/app/shared/MarkdownHelpSection';

// ============================================================================
// TYPES
// ============================================================================

type SupportTab =
  | 'tickets'
  | 'ai-chat'
  | 'knowledge-base'
  | 'csat'
  | 'surveys'
  | 'feedback'
  | 'forums'
  | 'onboarding'
  | 'assignment'
  | 'analytics'
  | 'session-replays';

type TicketPriority = 'urgent' | 'high' | 'medium' | 'low' | 'critical';
type TicketStatus = 'new' | 'open' | 'pending' | 'waiting_on_customer' | 'escalated' | 'resolved' | 'closed';

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  description?: string;
  customer_id?: string;
  customer_name?: string;
  customer_email?: string;
  priority: TicketPriority;
  status: TicketStatus;
  assigned_to?: string;
  assigned_to_name?: string;
  category_id?: string;
  category_name?: string;
  tags?: string[];
  source: string;
  sla_due_at?: string;
  sla_breached?: boolean;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  first_response_at?: string;
  linked_jira_key?: string;
  pipeline_status?: string;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  status: 'available' | 'busy' | 'away' | 'offline';
  current_ticket_count: number;
  max_concurrent_tickets: number;
  skills?: string[];
}

interface Category {
  id: string;
  name: string;
  description?: string;
  default_priority?: TicketPriority;
}

interface CSATStats {
  total_surveys: number;
  response_rate: number;
  average_rating: number;
  promoters: number;
  passives: number;
  detractors: number;
  trend: 'up' | 'down' | 'stable';
}

interface DashboardStats {
  open_tickets: number;
  urgent_tickets: number;
  avg_response_time_hours: number;
  avg_resolution_time_hours: number;
  csat_score: number;
  first_contact_resolution: number;
  tickets_today: number;
  resolved_today: number;
}

// ============================================================================
// PRIORITY & STATUS CONFIGS
// ============================================================================

const getPriorityConfig = (t: (key: string, fallback: string) => string): Record<TicketPriority, { label: string; color: string }> => ({
  critical: { label: t('platform.customerSupport.priority.critical', 'Critical'), color: 'bg-purple-100 text-purple-800 border-purple-300' },
  urgent: { label: t('platform.customerSupport.priority.urgent', 'Urgent'), color: 'bg-red-100 text-red-800 border-red-300' },
  high: { label: t('platform.customerSupport.priority.high', 'High'), color: 'bg-orange-100 text-orange-800 border-orange-300' },
  medium: { label: t('platform.customerSupport.priority.medium', 'Medium'), color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  low: { label: t('platform.customerSupport.priority.low', 'Low'), color: 'bg-gray-100 text-gray-800 border-gray-300' },
});

const getStatusConfig = (t: (key: string, fallback: string) => string): Record<TicketStatus, { label: string; color: string; icon: React.ReactNode }> => ({
  new: { label: t('platform.customerSupport.status.new', 'New'), color: 'bg-blue-100 text-blue-800', icon: <AlertCircle className="h-3 w-3" /> },
  open: { label: t('platform.customerSupport.status.open', 'Open'), color: 'bg-blue-100 text-blue-800', icon: <MessageSquare className="h-3 w-3" /> },
  pending: { label: t('platform.customerSupport.status.pending', 'Pending'), color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="h-3 w-3" /> },
  waiting_on_customer: { label: t('platform.customerSupport.status.waitingOnCustomer', 'Waiting'), color: 'bg-purple-100 text-purple-800', icon: <Clock className="h-3 w-3" /> },
  escalated: { label: t('platform.customerSupport.status.escalated', 'Escalated'), color: 'bg-red-100 text-red-800', icon: <AlertCircle className="h-3 w-3" /> },
  resolved: { label: t('platform.customerSupport.status.resolved', 'Resolved'), color: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" /> },
  closed: { label: t('platform.customerSupport.status.closed', 'Closed'), color: 'bg-gray-100 text-gray-800', icon: <XCircle className="h-3 w-3" /> },
});

// ============================================================================
// API HOOKS
// ============================================================================

function useTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async (filters?: Record<string, any>) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);

      const response = await apiClient.get(`/api/platform/v1/support/tickets?${params.toString()}`);
      setTickets(response.data.tickets || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching tickets:', err);
      setError(err.message);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return { tickets, loading, error, refetch: fetchTickets };
}

function useAgents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        // First try to get all organization users (for assignment dropdown)
        const response = await apiClient.get('/api/customer-support/assignment/agents/all');
        const agentsData = response.data.agents || [];
        setAgents(agentsData.map((a: any) => ({
          id: a.id,
          name: a.name || a.email || `Agent ${a.id.slice(0, 8)}`,
          email: a.email,
          status: a.status || 'available',
          current_ticket_count: a.current_ticket_count || 0,
          max_concurrent_tickets: a.max_concurrent_tickets || 20,
          skills: a.skills || [],
        })));
      } catch (err) {
        console.error('Error fetching agents:', err);
        // Fallback to available agents endpoint
        try {
          const fallback = await apiClient.get('/api/customer-support/assignment/agents/available');
          setAgents(fallback.data.agents || []);
        } catch {
          setAgents([]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);

  return { agents, loading };
}

function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get('/api/platform/v1/support/categories');
        setCategories(response.data.categories || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories([
          { id: '1', name: 'Technical Support', default_priority: 'medium' },
          { id: '2', name: 'Billing', default_priority: 'medium' },
          { id: '3', name: 'Account', default_priority: 'high' },
          { id: '4', name: 'Feature Request', default_priority: 'low' },
          { id: '5', name: 'Bug Report', default_priority: 'high' },
        ]);
      }
    };
    fetchCategories();
  }, []);

  return { categories };
}

function useDashboardStats() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiClient.get('/api/platform/v1/support/dashboard');
        setStats(response.data);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setStats({
          open_tickets: 12,
          urgent_tickets: 3,
          avg_response_time_hours: 2.4,
          avg_resolution_time_hours: 18.5,
          csat_score: 94,
          first_contact_resolution: 72,
          tickets_today: 8,
          resolved_today: 5,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return { stats, loading };
}

// ============================================================================
// CREATE TICKET MODAL
// ============================================================================

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  agents: Agent[];
  categories: Category[];
}

function CreateTicketModal({ isOpen, onClose, onSuccess, agents, categories }: CreateTicketModalProps) {
  const { user } = useAuth();
  const { t } = useAppTranslation();
  const [submitting, setSubmitting] = useState(false);
  const priorityConfig = getPriorityConfig(t);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    priority: 'medium' as TicketPriority,
    category_id: '',
    assigned_to: 'auto',
    tags: '',
    // Advanced issue capture fields
    affected_area: '',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    error_message: '',
    browser_info: '',
    impact_level: 'single_user' as 'single_user' | 'team' | 'department' | 'organization',
    urgency_reason: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Build extended description with structured issue details
      let extendedDescription = formData.description;
      if (showAdvanced) {
        const additionalDetails = [];
        if (formData.affected_area) additionalDetails.push(`**Affected Area:** ${formData.affected_area}`);
        if (formData.steps_to_reproduce) additionalDetails.push(`**Steps to Reproduce:**\n${formData.steps_to_reproduce}`);
        if (formData.expected_behavior) additionalDetails.push(`**Expected Behavior:** ${formData.expected_behavior}`);
        if (formData.actual_behavior) additionalDetails.push(`**Actual Behavior:** ${formData.actual_behavior}`);
        if (formData.error_message) additionalDetails.push(`**Error Message:** ${formData.error_message}`);
        if (formData.browser_info) additionalDetails.push(`**Browser/Device:** ${formData.browser_info}`);
        if (formData.impact_level !== 'single_user') additionalDetails.push(`**Impact Level:** ${formData.impact_level.replace('_', ' ')}`);
        if (formData.urgency_reason) additionalDetails.push(`**Urgency Reason:** ${formData.urgency_reason}`);

        if (additionalDetails.length > 0) {
          extendedDescription += '\n\n---\n### Issue Details\n' + additionalDetails.join('\n\n');
        }
      }

      await apiClient.post('/api/platform/v1/support/tickets', {
        subject: formData.subject,
        description: extendedDescription,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        priority: formData.priority,
        category_id: formData.category_id || undefined,
        assigned_to: formData.assigned_to && formData.assigned_to !== 'auto' ? formData.assigned_to : undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        source: 'portal',
        metadata: showAdvanced ? {
          affected_area: formData.affected_area,
          impact_level: formData.impact_level,
          browser_info: formData.browser_info,
        } : undefined,
      });
      onSuccess();
      onClose();
      setFormData({
        subject: '',
        description: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        priority: 'medium',
        category_id: '',
        assigned_to: 'auto',
        tags: '',
        affected_area: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        error_message: '',
        browser_info: '',
        impact_level: 'single_user',
        urgency_reason: '',
      });
      setShowAdvanced(false);
    } catch (err: any) {
      console.error('Error creating ticket:', err);
      alert('Failed to create ticket: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('platform.customerSupport.dialogs.createTicket.title', 'Create New Support Ticket')}</DialogTitle>
          <DialogDescription>
            {t('platform.customerSupport.dialogs.createTicket.description', 'Enter the details for the new support ticket. All fields marked with * are required.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <Label htmlFor="subject">{t('platform.customerSupport.dialogs.createTicket.subjectLabel', 'Subject')} *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
              placeholder={t('platform.customerSupport.dialogs.createTicket.subjectPlaceholder', 'Brief description of the issue')}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="description">{t('platform.customerSupport.dialogs.createTicket.descriptionLabel', 'Detailed Description')} *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              placeholder={t('platform.customerSupport.dialogs.createTicket.descriptionPlaceholder', 'Please provide detailed information about the issue, including steps to reproduce, expected behavior, and any error messages...')}
              rows={5}
              className="mt-1"
            />
          </div>

          <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <User className="h-4 w-4" />
                {t('platform.customerSupport.dialogs.createTicket.customerInfo', 'Customer Information')}
              </h4>
              {user && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({
                    ...formData,
                    customer_name: user.name || user.full_name || '',
                    customer_email: user.email || '',
                    customer_phone: user.phone || '',
                  })}
                  className="text-xs"
                >
                  <User className="h-3 w-3 mr-1" />
                  {t('platform.customerSupport.dialogs.createTicket.useMyInfo', 'Use my info')}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="customer_name">{t('platform.customerSupport.dialogs.createTicket.customerNameLabel', 'Customer Name')} *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  placeholder={t('platform.customerSupport.dialogs.createTicket.customerNamePlaceholder', 'Full name')}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="customer_email">{t('platform.customerSupport.dialogs.createTicket.emailLabel', 'Email')} *</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  required
                  placeholder={t('platform.customerSupport.dialogs.createTicket.emailPlaceholder', 'john@example.com')}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="customer_phone">{t('platform.customerSupport.dialogs.createTicket.phoneLabel', 'Phone (Optional)')}</Label>
              <PhoneInput
                id="customer_phone"
                value={formData.customer_phone}
                onChange={(v) => setFormData({ ...formData, customer_phone: v })}
                placeholder={t('platform.customerSupport.dialogs.createTicket.phonePlaceholder', '+1 (555) 123-4567')}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="priority">{t('platform.customerSupport.dialogs.createTicket.priorityLabel', 'Priority')}</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value as TicketPriority })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('platform.customerSupport.dialogs.createTicket.selectPriority', 'Select priority')} />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="low">{priorityConfig.low.label}</SelectItem>
                  <SelectItem value="medium">{priorityConfig.medium.label}</SelectItem>
                  <SelectItem value="high">{priorityConfig.high.label}</SelectItem>
                  <SelectItem value="urgent">{priorityConfig.urgent.label}</SelectItem>
                  <SelectItem value="critical">{priorityConfig.critical.label}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category">{t('platform.customerSupport.dialogs.createTicket.categoryLabel', 'Category')}</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('platform.customerSupport.dialogs.createTicket.selectCategory', 'Select category')} />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="assigned_to">{t('platform.customerSupport.dialogs.createTicket.assignToLabel', 'Assign To')}</Label>
            <Select
              value={formData.assigned_to}
              onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t('platform.customerSupport.dialogs.createTicket.assignToPlaceholder', 'Auto-assign or select agent')} />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="auto">{t('platform.customerSupport.dialogs.createTicket.autoAssign', 'Auto-Assign (Recommended)')}</SelectItem>
                {agents.length > 0 ? (
                  agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} ({agent.current_ticket_count}/{agent.max_concurrent_tickets} {t('platform.customerSupport.common.tickets', 'tickets')})
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-agents" disabled>{t('platform.customerSupport.dialogs.createTicket.noAgentsAvailable', 'No agents available')}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {t('platform.customerSupport.dialogs.createTicket.assignToHint', 'Leave empty to use intelligent auto-assignment based on skills and workload')}
            </p>
          </div>

          <div>
            <Label htmlFor="tags">{t('platform.customerSupport.dialogs.createTicket.tagsLabel', 'Tags')}</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder={t('platform.customerSupport.dialogs.createTicket.tagsPlaceholder', 'Enter tags separated by commas (e.g., billing, urgent, VIP)')}
              className="mt-1"
            />
          </div>

          {/* Advanced Issue Capture Section */}
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
              {showAdvanced ? t('platform.customerSupport.dialogs.createTicket.hideAdvanced', 'Hide Advanced Issue Details') : t('platform.customerSupport.dialogs.createTicket.showAdvanced', 'Show Advanced Issue Details')}
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4 border rounded-lg p-4 bg-blue-50">
                <h4 className="font-medium text-gray-900 flex items-center gap-2 text-sm">
                  <Settings className="h-4 w-4" />
                  {t('platform.customerSupport.dialogs.createTicket.detailedIssueCapture', 'Detailed Issue Capture (Optional)')}
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="affected_area">{t('platform.customerSupport.dialogs.createTicket.affectedAreaLabel', 'Affected Area/Feature')}</Label>
                    <Input
                      id="affected_area"
                      value={formData.affected_area}
                      onChange={(e) => setFormData({ ...formData, affected_area: e.target.value })}
                      placeholder={t('platform.customerSupport.dialogs.createTicket.affectedAreaPlaceholder', 'e.g., Dashboard, Reports, Login')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="impact_level">{t('platform.customerSupport.dialogs.createTicket.impactLevelLabel', 'Impact Level')}</Label>
                    <Select
                      value={formData.impact_level}
                      onValueChange={(value) => setFormData({ ...formData, impact_level: value as any })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t('platform.customerSupport.dialogs.createTicket.impactLevelPlaceholder', 'Who is affected?')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single_user">{t('platform.customerSupport.dialogs.createTicket.impactSingleUser', 'Single User')}</SelectItem>
                        <SelectItem value="team">{t('platform.customerSupport.dialogs.createTicket.impactTeam', 'Team (Multiple Users)')}</SelectItem>
                        <SelectItem value="department">{t('platform.customerSupport.dialogs.createTicket.impactDepartment', 'Department')}</SelectItem>
                        <SelectItem value="organization">{t('platform.customerSupport.dialogs.createTicket.impactOrganization', 'Entire Organization')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="steps_to_reproduce">{t('platform.customerSupport.dialogs.createTicket.stepsToReproduceLabel', 'Steps to Reproduce')}</Label>
                  <Textarea
                    id="steps_to_reproduce"
                    value={formData.steps_to_reproduce}
                    onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                    placeholder={t('platform.customerSupport.dialogs.createTicket.stepsToReproducePlaceholder', '1. Go to...\n2. Click on...\n3. Observe...')}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="expected_behavior">{t('platform.customerSupport.dialogs.createTicket.expectedBehaviorLabel', 'Expected Behavior')}</Label>
                    <Textarea
                      id="expected_behavior"
                      value={formData.expected_behavior}
                      onChange={(e) => setFormData({ ...formData, expected_behavior: e.target.value })}
                      placeholder={t('platform.customerSupport.dialogs.createTicket.expectedBehaviorPlaceholder', 'What should happen...')}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="actual_behavior">{t('platform.customerSupport.dialogs.createTicket.actualBehaviorLabel', 'Actual Behavior')}</Label>
                    <Textarea
                      id="actual_behavior"
                      value={formData.actual_behavior}
                      onChange={(e) => setFormData({ ...formData, actual_behavior: e.target.value })}
                      placeholder={t('platform.customerSupport.dialogs.createTicket.actualBehaviorPlaceholder', 'What actually happens...')}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="error_message">{t('platform.customerSupport.dialogs.createTicket.errorMessageLabel', 'Error Message (if any)')}</Label>
                  <Input
                    id="error_message"
                    value={formData.error_message}
                    onChange={(e) => setFormData({ ...formData, error_message: e.target.value })}
                    placeholder={t('platform.customerSupport.dialogs.createTicket.errorMessagePlaceholder', 'Copy and paste any error message...')}
                    className="mt-1 font-mono text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="browser_info">{t('platform.customerSupport.dialogs.createTicket.browserInfoLabel', 'Browser/Device Info')}</Label>
                    <Input
                      id="browser_info"
                      value={formData.browser_info}
                      onChange={(e) => setFormData({ ...formData, browser_info: e.target.value })}
                      placeholder={t('platform.customerSupport.dialogs.createTicket.browserInfoPlaceholder', 'e.g., Chrome 120, iPhone 15')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="urgency_reason">{t('platform.customerSupport.dialogs.createTicket.urgencyReasonLabel', 'Urgency Reason (if urgent/critical)')}</Label>
                    <Input
                      id="urgency_reason"
                      value={formData.urgency_reason}
                      onChange={(e) => setFormData({ ...formData, urgency_reason: e.target.value })}
                      placeholder={t('platform.customerSupport.dialogs.createTicket.urgencyReasonPlaceholder', 'e.g., Client demo tomorrow')}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {t('platform.customerSupport.common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('platform.customerSupport.dialogs.createTicket.creating', 'Creating...')}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('platform.customerSupport.dialogs.createTicket.createButton', 'Create Ticket')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TICKETS TAB
// ============================================================================

interface TicketsTabProps {
  stats: DashboardStats | null;
  onCreateTicket: () => void;
}

function TicketsTab({ stats, onCreateTicket }: TicketsTabProps) {
  const { t } = useAppTranslation();
  const priorityConfig = getPriorityConfig(t);
  const statusConfig = getStatusConfig(t);
  const { tickets, loading, refetch } = useTickets();
  const { agents } = useAgents();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [ticketDetails, setTicketDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [editingTicket, setEditingTicket] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [assignAgentId, setAssignAgentId] = useState<string>('');
  const [assigning, setAssigning] = useState(false);

  const handleViewTicket = async (ticket: any) => {
    setSelectedTicket(ticket);
    setLoadingDetails(true);
    try {
      const response = await apiClient.get(`/api/customer-support/tickets/${ticket.id}`);
      setTicketDetails(response.data.ticket);
      setEditForm({
        subject: response.data.ticket.subject,
        description: response.data.ticket.description,
        priority: response.data.ticket.priority,
        status: response.data.ticket.status,
        tags: response.data.ticket.tags?.join(', ') || '',
      });
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      setTicketDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedTicket(null);
    setTicketDetails(null);
    setEditingTicket(false);
    setNewComment('');
    setAssignAgentId('');
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    setAddingComment(true);
    try {
      await apiClient.post(`/api/customer-support/tickets/${selectedTicket.id}/comments`, {
        body: newComment,
        comment_type: isInternalNote ? 'internal_note' : 'reply',
        is_public: !isInternalNote,
      });
      // Refresh ticket details
      const response = await apiClient.get(`/api/customer-support/tickets/${selectedTicket.id}`);
      setTicketDetails(response.data.ticket);
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selectedTicket) return;
    try {
      await apiClient.put(`/api/customer-support/tickets/${selectedTicket.id}`, {
        status: newStatus,
      });
      // Refresh ticket details
      const response = await apiClient.get(`/api/customer-support/tickets/${selectedTicket.id}`);
      setTicketDetails(response.data.ticket);
      refetch();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedTicket) return;
    setSavingEdit(true);
    try {
      const updateData: any = {};
      if (editForm.subject !== ticketDetails?.subject) updateData.subject = editForm.subject;
      if (editForm.description !== ticketDetails?.description) updateData.description = editForm.description;
      if (editForm.priority !== ticketDetails?.priority) updateData.priority = editForm.priority;
      if (editForm.status !== ticketDetails?.status) updateData.status = editForm.status;

      const tagsArray = editForm.tags ? editForm.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [];
      const currentTags = ticketDetails?.tags || [];
      if (JSON.stringify(tagsArray) !== JSON.stringify(currentTags)) updateData.tags = tagsArray;

      if (Object.keys(updateData).length > 0) {
        await apiClient.put(`/api/customer-support/tickets/${selectedTicket.id}`, updateData);
        // Refresh ticket details
        const response = await apiClient.get(`/api/customer-support/tickets/${selectedTicket.id}`);
        setTicketDetails(response.data.ticket);
        refetch();
      }
      setEditingTicket(false);
    } catch (err) {
      console.error('Error saving edit:', err);
      alert('Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResolveTicket = async () => {
    if (!selectedTicket) return;
    if (!confirm('Are you sure you want to resolve this ticket?')) return;
    try {
      await apiClient.put(`/api/customer-support/tickets/${selectedTicket.id}`, {
        status: 'resolved',
      });
      // Refresh ticket details
      const response = await apiClient.get(`/api/customer-support/tickets/${selectedTicket.id}`);
      setTicketDetails(response.data.ticket);
      refetch();
    } catch (err) {
      console.error('Error resolving ticket:', err);
      alert('Failed to resolve ticket');
    }
  };

  const handleAssignTicket = async () => {
    if (!selectedTicket || !assignAgentId) return;
    setAssigning(true);
    try {
      await apiClient.post(`/api/customer-support/tickets/${selectedTicket.id}/assign`, {
        assigned_to: assignAgentId,
      });
      // Refresh ticket details
      const response = await apiClient.get(`/api/customer-support/tickets/${selectedTicket.id}`);
      setTicketDetails(response.data.ticket);
      setAssignAgentId('');
      refetch();
    } catch (err) {
      console.error('Error assigning ticket:', err);
      alert('Failed to assign ticket');
    } finally {
      setAssigning(false);
    }
  };

  /**
   * Open a ticket attachment.
   *
   * Two hops on purpose: this call is authenticated, and the API answers with a
   * short-lived presigned S3 URL rather than redirecting — following a redirect
   * would forward our Authorization header to S3 alongside the signature, which
   * S3 rejects. Local-storage deployments stream the bytes back instead, so fall
   * back to a blob URL when there's no `url` in the response.
   */
  const handleOpenAttachment = async (ticketId: string, attachmentId: string) => {
    try {
      const res = await apiClient.get(
        `/api/customer-support/tickets/${ticketId}/attachments/${attachmentId}`,
        { responseType: 'json' }
      );
      const url = res.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      const blobRes = await apiClient.get(
        `/api/customer-support/tickets/${ticketId}/attachments/${attachmentId}`,
        { responseType: 'blob' }
      );
      const objectUrl = URL.createObjectURL(blobRes.data);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      console.error('Error opening attachment:', err);
      alert('Failed to open attachment');
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!ticket.subject.toLowerCase().includes(query) &&
          !ticket.ticket_number?.toLowerCase().includes(query) &&
          !ticket.customer_name?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (statusFilter && ticket.status !== statusFilter) return false;
    if (priorityFilter && ticket.priority !== priorityFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.tickets.stats.openTickets', 'Open Tickets')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.open_tickets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.tickets.stats.urgentCritical', 'Urgent/Critical')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.urgent_tickets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Clock className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.tickets.stats.avgResponse', 'Avg Response')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.avg_response_time_hours?.toFixed(1) || '0'}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Award className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.tickets.stats.csatScore', 'CSAT Score')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.csat_score || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t('platform.customerSupport.tickets.searchPlaceholder', 'Search tickets...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder={t('platform.customerSupport.tickets.allStatuses', 'All Statuses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">{t('platform.customerSupport.tickets.allStatuses', 'All Statuses')}</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder={t('platform.customerSupport.tickets.allPriorities', 'All Priorities')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">{t('platform.customerSupport.tickets.allPriorities', 'All Priorities')}</SelectItem>
              {Object.entries(priorityConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <Button onClick={onCreateTicket}>
          <Plus className="h-4 w-4 mr-2" />
          {t('platform.customerSupport.tickets.newTicket', 'New Ticket')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('platform.customerSupport.tickets.noTicketsFound', 'No tickets found')}</p>
              <Button variant="outline" className="mt-4" onClick={onCreateTicket}>
                {t('platform.customerSupport.tickets.createFirstTicket', 'Create First Ticket')}
              </Button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.customerSupport.tickets.table.ticket', 'Ticket')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.customerSupport.tickets.table.customer', 'Customer')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.customerSupport.tickets.table.priority', 'Priority')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.customerSupport.tickets.table.status', 'Status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.customerSupport.tickets.table.assignee', 'Assignee')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.customerSupport.tickets.table.created', 'Created')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('platform.customerSupport.tickets.table.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-blue-600 text-sm">
                          {ticket.ticket_number || `#${ticket.id.slice(0, 8)}`}
                        </div>
                        <div className="text-sm text-gray-900 truncate max-w-xs">{ticket.subject}</div>
                        {ticket.sla_breached && (
                          <Badge variant="destructive" className="mt-1 text-xs">{t('platform.customerSupport.tickets.slaBreached', 'SLA Breached')}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{ticket.customer_name || t('platform.customerSupport.common.unknown', 'Unknown')}</div>
                      <div className="text-xs text-gray-500">{ticket.customer_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={priorityConfig[ticket.priority]?.color || 'bg-gray-100'}>
                        {priorityConfig[ticket.priority]?.label || ticket.priority}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className={statusConfig[ticket.status]?.color || ''}>
                        {statusConfig[ticket.status]?.icon}
                        <span className="ml-1">{statusConfig[ticket.status]?.label || ticket.status}</span>
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ticket.assigned_to_name || <span className="text-gray-400">{t('platform.customerSupport.tickets.unassigned', 'Unassigned')}</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleViewTicket(ticket)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Panel */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && handleCloseDetail()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-blue-600 font-mono">
                  {ticketDetails?.ticket_number || selectedTicket?.ticket_number || 'Loading...'}
                </span>
                {ticketDetails?.sla_breached && (
                  <Badge variant="destructive">{t('platform.customerSupport.tickets.slaBreached', 'SLA Breached')}</Badge>
                )}
              </div>
              {ticketDetails && !editingTicket && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingTicket(true)}>
                    <Edit className="h-4 w-4 mr-1" /> {t('platform.customerSupport.common.edit', 'Edit')}
                  </Button>
                  {ticketDetails.status !== 'resolved' && ticketDetails.status !== 'closed' && (
                    <Button variant="default" size="sm" onClick={handleResolveTicket}>
                      <CheckCircle className="h-4 w-4 mr-1" /> {t('platform.customerSupport.tickets.resolve', 'Resolve')}
                    </Button>
                  )}
                </div>
              )}
            </DialogTitle>
            <DialogDescription>
              {editingTicket ? t('platform.customerSupport.dialogs.ticketDetail.editDescription', 'Edit ticket details') : t('platform.customerSupport.dialogs.ticketDetail.viewDescription', 'View and manage ticket')}
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : ticketDetails ? (
            <div className="space-y-6">
              {/* Ticket Info Section */}
              {editingTicket ? (
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <Label>{t('platform.customerSupport.dialogs.ticketDetail.subjectLabel', 'Subject')}</Label>
                    <Input
                      value={editForm.subject || ''}
                      onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t('platform.customerSupport.dialogs.ticketDetail.descriptionLabel', 'Description')}</Label>
                    <Textarea
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('platform.customerSupport.dialogs.ticketDetail.priorityLabel', 'Priority')}</Label>
                      <Select value={editForm.priority || ''} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(priorityConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('platform.customerSupport.dialogs.ticketDetail.statusLabel', 'Status')}</Label>
                      <Select value={editForm.status || ''} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>{t('platform.customerSupport.dialogs.ticketDetail.tagsLabel', 'Tags (comma-separated)')}</Label>
                    <Input
                      value={editForm.tags || ''}
                      onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                      placeholder={t('platform.customerSupport.dialogs.ticketDetail.tagsPlaceholder', 'bug, urgent, api')}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditingTicket(false)}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
                    <Button onClick={handleSaveEdit} disabled={savingEdit}>
                      {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      {t('platform.customerSupport.common.saveChanges', 'Save Changes')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg mb-2">{ticketDetails.subject}</h3>
                  <p className="text-gray-600 whitespace-pre-wrap mb-4">{ticketDetails.description || t('platform.customerSupport.tickets.noDescription', 'No description provided')}</p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.priority', 'Priority')}:</span>
                      <Badge className={`ml-2 ${priorityConfig[ticketDetails.priority]?.color || 'bg-gray-100'}`}>
                        {priorityConfig[ticketDetails.priority]?.label || ticketDetails.priority}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.status', 'Status')}:</span>
                      <Select value={ticketDetails.status} onValueChange={handleStatusChange}>
                        <SelectTrigger className="inline-flex ml-2 h-7 w-auto min-w-[120px]">
                          <Badge variant="outline" className={statusConfig[ticketDetails.status]?.color || ''}>
                            {statusConfig[ticketDetails.status]?.icon}
                            <span className="ml-1">{statusConfig[ticketDetails.status]?.label || ticketDetails.status}</span>
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.category', 'Category')}:</span>
                      <span className="ml-2 text-gray-900">{ticketDetails.category_name || t('platform.customerSupport.tickets.uncategorized', 'Uncategorized')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.source', 'Source')}:</span>
                      <span className="ml-2 text-gray-900 capitalize">{ticketDetails.source || t('platform.customerSupport.common.unknown', 'Unknown')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.created', 'Created')}:</span>
                      <span className="ml-2 text-gray-900">{new Date(ticketDetails.created_at).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.updated', 'Updated')}:</span>
                      <span className="ml-2 text-gray-900">{new Date(ticketDetails.updated_at).toLocaleString()}</span>
                    </div>
                    {ticketDetails.sla_due_at && (
                      <div>
                        <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.slaDue', 'SLA Due')}:</span>
                        <span className={`ml-2 ${ticketDetails.sla_breached ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                          {new Date(ticketDetails.sla_due_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {ticketDetails.resolution_at && (
                      <div>
                        <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.resolved', 'Resolved')}:</span>
                        <span className="ml-2 text-green-600">{new Date(ticketDetails.resolution_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Assignment */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <UserCheck className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.assignee', 'Assignee')}:</span>
                      <span className="font-medium text-gray-900">
                        {ticketDetails.assigned_to_name || t('platform.customerSupport.tickets.unassigned', 'Unassigned')}
                      </span>
                    </div>
                    {agents.length === 0 ? (
                      <p className="text-xs text-gray-500">
                        {t('platform.customerSupport.tickets.detail.noAgents', 'No agents available — invite team members')}
                      </p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Select value={assignAgentId} onValueChange={setAssignAgentId}>
                          <SelectTrigger className="h-8 w-56">
                            <SelectValue placeholder={t('platform.customerSupport.tickets.detail.selectAgent', 'Select agent…')} />
                          </SelectTrigger>
                          <SelectContent>
                            {agents.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id}>
                                {agent.name || agent.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={handleAssignTicket} disabled={!assignAgentId || assigning}>
                          {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
                          {t('platform.customerSupport.tickets.detail.assign', 'Assign')}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Jira tracking */}
                  {ticketDetails.linked_jira_key && (
                    <div className="mt-4 pt-4 border-t flex items-center gap-2 text-sm">
                      <span className="text-gray-500">{t('platform.customerSupport.tickets.detail.jira', 'Jira')}:</span>
                      <a
                        href={`https://psyssurance.atlassian.net/browse/${ticketDetails.linked_jira_key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-mono inline-flex items-center gap-1"
                      >
                        {ticketDetails.linked_jira_key}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      {ticketDetails.pipeline_status && (
                        <Badge variant="outline" className="text-xs">{ticketDetails.pipeline_status}</Badge>
                      )}
                    </div>
                  )}

                  {/* Where the reporter was. The widget sends a breadcrumb
                      (screen_path) built from the app's declared view; older
                      tickets only carry the single-heading `screen` guess. */}
                  {(ticketDetails.metadata?.screen_path || ticketDetails.metadata?.screen || ticketDetails.metadata?.page_url) && (
                    <div className="mt-4 pt-4 border-t text-sm space-y-1">
                      {(ticketDetails.metadata.screen_path || ticketDetails.metadata.screen) && (
                        <div className="flex items-start gap-2">
                          <Monitor className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
                          <span className="text-gray-500 shrink-0">
                            {t('platform.customerSupport.tickets.detail.screen', 'Screen')}:
                          </span>
                          <span className="font-medium text-gray-900 break-words">
                            {ticketDetails.metadata.screen_path || ticketDetails.metadata.screen}
                          </span>
                        </div>
                      )}
                      {ticketDetails.metadata.page_url && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 shrink-0 pl-6">
                            {t('platform.customerSupport.tickets.detail.page', 'Page')}:
                          </span>
                          <span className="text-gray-600 break-all text-xs">{ticketDetails.metadata.page_url}</span>
                        </div>
                      )}
                      {Array.isArray(ticketDetails.metadata.recent_screens) && ticketDetails.metadata.recent_screens.length > 1 && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 shrink-0 pl-6">
                            {t('platform.customerSupport.tickets.detail.recentScreens', 'Trail')}:
                          </span>
                          <span className="text-gray-600 text-xs break-words">
                            {ticketDetails.metadata.recent_screens
                              .map((s: any) => (typeof s === 'string' ? s : s?.label))
                              .filter(Boolean)
                              .join(' → ')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Evidence the reporter dropped into the widget */}
                  {ticketDetails.attachments && ticketDetails.attachments.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <span className="text-gray-500 text-sm flex items-center gap-2">
                        <Paperclip className="h-4 w-4" />
                        {t('platform.customerSupport.tickets.detail.attachments', 'Attachments')} ({ticketDetails.attachments.length})
                      </span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {ticketDetails.attachments.map((att: any) => (
                          <button
                            key={att.id}
                            type="button"
                            onClick={() => handleOpenAttachment(ticketDetails.id, att.id)}
                            className="inline-flex items-center gap-2 border rounded-lg px-3 py-2 text-xs bg-white hover:bg-gray-50"
                          >
                            <Paperclip className="h-3 w-3 text-gray-400" />
                            <span className="font-medium text-gray-900 max-w-[220px] truncate">{att.filename}</span>
                            <span className="text-gray-400">
                              {att.size_bytes ? `${Math.max(1, Math.round(att.size_bytes / 1024))} KB` : ''}
                            </span>
                            <ExternalLink className="h-3 w-3 text-gray-400" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {ticketDetails.tags && ticketDetails.tags.length > 0 && (
                    <div className="mt-4">
                      <span className="text-gray-500 text-sm">{t('platform.customerSupport.tickets.detail.tags', 'Tags')}:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ticketDetails.tags.map((tag: string, i: number) => (
                          <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Comments/Activity Section */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  {t('platform.customerSupport.tickets.commentsActivity', 'Comments & Activity')} ({ticketDetails.comments?.length || 0})
                </h4>

                <div className="space-y-3 max-h-[300px] overflow-y-auto border rounded-lg p-3 bg-white">
                  {(!ticketDetails.comments || ticketDetails.comments.length === 0) ? (
                    <p className="text-gray-500 text-center py-4">{t('platform.customerSupport.tickets.noComments', 'No comments yet')}</p>
                  ) : (
                    ticketDetails.comments.map((comment: any) => (
                      <div
                        key={comment.id}
                        className={`p-3 rounded-lg ${
                          comment.comment_type === 'internal_note'
                            ? 'bg-yellow-50 border border-yellow-200'
                            : comment.author_type === 'customer'
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">
                              {comment.author_name || comment.author_email || 'System'}
                            </span>
                            {comment.comment_type === 'internal_note' && (
                              <Badge variant="outline" className="text-xs bg-yellow-100">{t('platform.customerSupport.tickets.internalNote', 'Internal Note')}</Badge>
                            )}
                            {comment.author_type === 'system' && (
                              <Badge variant="outline" className="text-xs">{t('platform.customerSupport.tickets.system', 'System')}</Badge>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.body}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Comment Form */}
                <div className="mt-4 space-y-3">
                  <Textarea
                    placeholder={t('platform.customerSupport.tickets.commentPlaceholder', 'Add a comment or internal note...')}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={3}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-gray-600">{t('platform.customerSupport.tickets.internalNoteHint', 'Internal note (not visible to customer)')}</span>
                    </label>
                    <Button onClick={handleAddComment} disabled={!newComment.trim() || addingComment}>
                      {addingComment ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                      {isInternalNote ? t('platform.customerSupport.tickets.addNote', 'Add Note') : t('platform.customerSupport.tickets.addReply', 'Add Reply')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Integration Hooks Section */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  {t('platform.customerSupport.tickets.integrations', 'Integrations')}
                </h4>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled title={t('platform.customerSupport.tickets.devTaskComingSoon', 'Development task tracking coming soon')}>
                    <span className="mr-1">📋</span> {t('platform.customerSupport.tickets.createDevTask', 'Create Dev Task')}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('platform.customerSupport.tickets.integrationsComingSoon', 'Development task tracking integration coming soon.')}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t('platform.customerSupport.tickets.failedToLoadDetails', 'Failed to load ticket details')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// AI CHAT TAB
// ============================================================================

interface AIChatConfig {
  is_enabled: boolean;
  kb_integration_enabled: boolean;
  human_handoff_enabled: boolean;
  auto_escalation_threshold: number;
  greeting_message: string;
  fallback_message: string;
  max_conversation_length: number;
  confidence_threshold: number;
}

function AIChatTab() {
  const { t } = useAppTranslation();
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const [conversationDetails, setConversationDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [config, setConfig] = useState<AIChatConfig>({
    is_enabled: true,
    kb_integration_enabled: true,
    human_handoff_enabled: true,
    auto_escalation_threshold: 3,
    greeting_message: '',
    fallback_message: '',
    max_conversation_length: 50,
    confidence_threshold: 0.7,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [convResponse, statsResponse, configResponse] = await Promise.all([
          apiClient.get('/api/customer-support/ai-chat/conversations'),
          apiClient.get('/api/customer-support/ai-chat/stats'),
          apiClient.get('/api/customer-support/ai-chat/admin/config'),
        ]);
        setConversations(convResponse.data.conversations || []);
        setStats(statsResponse.data);
        if (configResponse.data.config) {
          setConfig(configResponse.data.config);
        }
      } catch (err) {
        console.error('Error fetching AI chat data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSelectConversation = async (conv: any) => {
    setSelectedConversation(conv);
    setLoadingDetails(true);
    try {
      const response = await apiClient.get(`/api/support/ai-chat/public/chat/conversation/${conv.id}`);
      setConversationDetails(response.data);
    } catch (err) {
      console.error('Error fetching conversation details:', err);
      setConversationDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleToggleConfig = async (key: keyof AIChatConfig, value: boolean) => {
    const previousValue = config[key];
    // Optimistic update
    setConfig(prev => ({ ...prev, [key]: value }));

    try {
      await apiClient.put('/api/customer-support/ai-chat/admin/config', { [key]: value });
    } catch (err) {
      // Revert on error
      setConfig(prev => ({ ...prev, [key]: previousValue }));
      console.error('Failed to update config:', err);
    }
  };

  const handleSaveAdvancedConfig = async () => {
    setSavingConfig(true);
    try {
      await apiClient.put('/api/customer-support/ai-chat/admin/config', config);
      setShowConfigModal(false);
    } catch (err: any) {
      alert('Failed to save config: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Bot className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.aiChat.stats.conversations', 'AI Conversations')}</p>
                <p className="text-2xl font-bold">{stats?.total_conversations || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.aiChat.stats.resolutionRate', 'AI Resolution Rate')}</p>
                <p className="text-2xl font-bold">{stats?.ai_resolution_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Zap className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.aiChat.stats.avgResponseTime', 'Avg Response Time')}</p>
                <p className="text-2xl font-bold">{stats?.avg_response_time || '< 1'}s</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ThumbsUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.aiChat.stats.satisfaction', 'Satisfaction')}</p>
                <p className="text-2xl font-bold">{stats?.satisfaction_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                {t('platform.customerSupport.aiChat.config.title', 'AI Chatbot Configuration')}
              </CardTitle>
              <CardDescription>
                {t('platform.customerSupport.aiChat.config.description', 'Configure your AI support chatbot powered by Claude')}
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => setShowConfigModal(true)}>
              <Settings className="h-4 w-4 mr-2" />
              {t('platform.customerSupport.aiChat.config.advancedSettings', 'Advanced Settings')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium">{t('platform.customerSupport.aiChat.config.chatbotStatus', 'AI Chatbot Status')}</h4>
                  <p className="text-sm text-gray-500">{t('platform.customerSupport.aiChat.config.chatbotStatusDesc', 'Enable/disable the AI chatbot')}</p>
                </div>
                <button
                  onClick={() => handleToggleConfig('is_enabled', !config.is_enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.is_enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.is_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium">{t('platform.customerSupport.aiChat.config.kbIntegration', 'Knowledge Base Integration')}</h4>
                  <p className="text-sm text-gray-500">{t('platform.customerSupport.aiChat.config.kbIntegrationDesc', 'AI uses KB articles for answers')}</p>
                </div>
                <button
                  onClick={() => handleToggleConfig('kb_integration_enabled', !config.kb_integration_enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.kb_integration_enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.kb_integration_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium">{t('platform.customerSupport.aiChat.config.humanHandoff', 'Human Handoff')}</h4>
                  <p className="text-sm text-gray-500">{t('platform.customerSupport.aiChat.config.humanHandoffDesc', 'Auto-escalate complex issues')}</p>
                </div>
                <button
                  onClick={() => handleToggleConfig('human_handoff_enabled', !config.human_handoff_enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.human_handoff_enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.human_handoff_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">{t('platform.customerSupport.aiChat.config.embedWidget', 'Embed Widget')}</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                  {t('platform.customerSupport.aiChat.config.embedWidgetDesc', 'Add the support widget to your website:')}
                </p>
                <code className="block p-2 bg-white dark:bg-gray-800 rounded text-xs overflow-x-auto">
                  {`<script src="/widgets/support-widget.js"></script>`}
                </code>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">{t('platform.customerSupport.aiChat.config.currentStatus', 'Current Status')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t('platform.customerSupport.aiChat.config.chatbot', 'Chatbot')}</span>
                    <Badge className={config.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {config.is_enabled ? t('platform.customerSupport.aiChat.config.active', 'Active') : t('platform.customerSupport.aiChat.config.disabled', 'Disabled')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t('platform.customerSupport.aiChat.config.knowledgeBase', 'Knowledge Base')}</span>
                    <Badge className={config.kb_integration_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {config.kb_integration_enabled ? t('platform.customerSupport.aiChat.config.connected', 'Connected') : t('platform.customerSupport.aiChat.config.disabled', 'Disabled')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t('platform.customerSupport.aiChat.config.confidenceThreshold', 'Confidence Threshold')}</span>
                    <span className="font-medium">{(config.confidence_threshold * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Config Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('platform.customerSupport.aiChat.dialogs.advancedSettings.title', 'Advanced AI Chatbot Settings')}</DialogTitle>
            <DialogDescription>{t('platform.customerSupport.aiChat.dialogs.advancedSettings.description', 'Configure advanced settings for the AI chatbot')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('platform.customerSupport.aiChat.dialogs.advancedSettings.greetingLabel', 'Greeting Message')}</Label>
              <Textarea
                value={config.greeting_message}
                onChange={(e) => setConfig({ ...config, greeting_message: e.target.value })}
                placeholder={t('platform.customerSupport.aiChat.dialogs.advancedSettings.greetingPlaceholder', "Hi! I'm your AI support assistant. How can I help you today?")}
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.aiChat.dialogs.advancedSettings.fallbackLabel', 'Fallback Message')}</Label>
              <Textarea
                value={config.fallback_message}
                onChange={(e) => setConfig({ ...config, fallback_message: e.target.value })}
                placeholder={t('platform.customerSupport.aiChat.dialogs.advancedSettings.fallbackPlaceholder', "I'm not sure how to help with that. Would you like to speak with a human agent?")}
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('platform.customerSupport.aiChat.dialogs.advancedSettings.confidenceLabel', 'Confidence Threshold')}</Label>
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={config.confidence_threshold}
                  onChange={(e) => setConfig({ ...config, confidence_threshold: parseFloat(e.target.value) })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">{t('platform.customerSupport.aiChat.dialogs.advancedSettings.confidenceHint', 'Min confidence for AI to respond (0-1)')}</p>
              </div>
              <div>
                <Label>{t('platform.customerSupport.aiChat.dialogs.advancedSettings.escalationLabel', 'Auto-Escalation Threshold')}</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={config.auto_escalation_threshold}
                  onChange={(e) => setConfig({ ...config, auto_escalation_threshold: parseInt(e.target.value) })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">{t('platform.customerSupport.aiChat.dialogs.advancedSettings.escalationHint', 'Low-confidence responses before escalation')}</p>
              </div>
            </div>
            <div>
              <Label>{t('platform.customerSupport.aiChat.dialogs.advancedSettings.maxConversationLabel', 'Max Conversation Length')}</Label>
              <Input
                type="number"
                min={5}
                max={100}
                value={config.max_conversation_length}
                onChange={(e) => setConfig({ ...config, max_conversation_length: parseInt(e.target.value) })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">{t('platform.customerSupport.aiChat.dialogs.advancedSettings.maxConversationHint', 'Maximum messages before suggesting human handoff')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
            <Button onClick={handleSaveAdvancedConfig} disabled={savingConfig}>
              {savingConfig ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('platform.customerSupport.common.saving', 'Saving...')}
                </>
              ) : (
                t('platform.customerSupport.common.saveSettings', 'Save Settings')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('platform.customerSupport.aiChat.recentConversations', 'Recent AI Conversations')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('platform.customerSupport.aiChat.noConversations', 'No AI conversations yet')}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {conversations.slice(0, 20).map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedConversation?.id === conv.id
                        ? 'bg-blue-50 border-blue-300'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div>
                      <p className="font-medium">{conv.contact_name || conv.contact_email || 'Anonymous'}</p>
                      <p className="text-sm text-gray-500">{conv.total_messages} {t('platform.customerSupport.common.messages', 'messages')}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant={conv.status === 'resolved' ? 'default' : conv.status === 'escalated' ? 'destructive' : 'secondary'}>
                        {conv.status}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {new Date(conv.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversation Detail Panel */}
        <Card>
          <CardHeader>
            <CardTitle>{t('platform.customerSupport.aiChat.conversationDetails', 'Conversation Details')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedConversation ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('platform.customerSupport.aiChat.selectConversation', 'Select a conversation to view details')}</p>
              </div>
            ) : loadingDetails ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : !conversationDetails ? (
              <div className="text-center py-8 text-gray-500">
                <p>{t('platform.customerSupport.aiChat.loadFailed', 'Could not load conversation details')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Conversation Info */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">{t('platform.customerSupport.aiChat.contact', 'Contact')}:</span> {conversationDetails.contact_name || conversationDetails.contact_email || t('platform.customerSupport.common.anonymous', 'Anonymous')}</div>
                    <div><span className="text-gray-500">{t('platform.customerSupport.aiChat.status', 'Status')}:</span> {conversationDetails.status}</div>
                    <div><span className="text-gray-500">{t('platform.customerSupport.aiChat.messagesCount', 'Messages')}:</span> {conversationDetails.total_messages}</div>
                    <div><span className="text-gray-500">{t('platform.customerSupport.aiChat.aiHandled', 'AI Handled')}:</span> {conversationDetails.ai_handled ? t('platform.customerSupport.common.yes', 'Yes') : t('platform.customerSupport.common.no', 'No')}</div>
                  </div>
                </div>

                {/* Messages */}
                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {conversationDetails.messages?.length > 0 ? (
                    conversationDetails.messages.map((msg: any, idx: number) => (
                      <div
                        key={msg.id || idx}
                        className={`p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-blue-50 ml-8'
                            : 'bg-gray-100 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">
                            {msg.role === 'user' ? t('platform.customerSupport.aiChat.customer', 'Customer') : t('platform.customerSupport.aiChat.aiAssistant', 'AI Assistant')}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm">{msg.content}</p>
                        {msg.confidence_score && (
                          <p className="text-xs text-gray-400 mt-1">
                            {t('platform.customerSupport.aiChat.confidence', 'Confidence')}: {(msg.confidence_score * 100).toFixed(0)}%
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-4">{t('platform.customerSupport.aiChat.noMessagesInConversation', 'No messages in this conversation')}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// CSAT TAB
// ============================================================================

function CSATTab() {
  const { t } = useAppTranslation();
  const [stats, setStats] = useState<CSATStats | null>(null);
  const [responses, setResponses] = useState<any[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [summary, setSummary] = useState<{
    total: number;
    average_score: number;
    distribution: Record<string, number>;
    positive: number;
    satisfaction_rate: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState({
    is_enabled: true,
    auto_send: true,
    delay_hours: 24,
    thank_you_message: 'Thank you for your feedback!',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, responsesRes, configRes, summaryRes] = await Promise.all([
          apiClient.get('/api/customer-support/csat/analytics'),
          apiClient.get('/api/customer-support/csat/responses'),
          apiClient.get('/api/customer-support/csat/config'),
          apiClient.get('/api/customer-support/csat/summary'),
        ]);
        setStats(statsRes.data.analytics || statsRes.data);
        setResponses(responsesRes.data.responses || []);
        setConfig(configRes.data.config);
        setSummary(summaryRes.data || null);
        if (configRes.data.config) {
          setConfigForm({
            is_enabled: configRes.data.config.is_enabled ?? true,
            auto_send: configRes.data.config.auto_send ?? true,
            delay_hours: configRes.data.config.delay_hours ?? 24,
            thank_you_message: configRes.data.config.thank_you_message ?? 'Thank you for your feedback!',
          });
        }
      } catch (err) {
        console.error('Error fetching CSAT data:', err);
        setStats({
          total_surveys: 245,
          response_rate: 42,
          average_rating: 4.2,
          promoters: 156,
          passives: 52,
          detractors: 37,
          trend: 'up',
        });
        setResponses([
          { id: '1', rating: 5, feedback: 'Great support!', ticket_number: 'TKT-001', responded_at: new Date().toISOString() },
          { id: '2', rating: 4, feedback: 'Quick response time', ticket_number: 'TKT-002', responded_at: new Date().toISOString() },
          { id: '3', rating: 3, feedback: 'Could be faster', ticket_number: 'TKT-003', responded_at: new Date().toISOString() },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveConfig = async () => {
    try {
      await apiClient.put('/api/customer-support/csat/config', configForm);
      setShowConfigModal(false);
      alert('CSAT configuration saved successfully!');
    } catch (err: any) {
      alert('Failed to save config: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">{t('platform.customerSupport.csat.title', 'Customer Satisfaction Surveys')}</h2>
          <p className="text-sm text-gray-500">{t('platform.customerSupport.csat.description', 'Track and analyze customer satisfaction after ticket resolution')}</p>
        </div>
        <Button variant="outline" onClick={() => setShowConfigModal(true)}>
          <Settings className="h-4 w-4 mr-2" />
          {t('platform.customerSupport.csat.configure', 'Configure CSAT')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.csat.stats.avgRating', 'Average Rating')}</p>
                <p className="text-2xl font-bold">{stats?.average_rating?.toFixed(1) || '0'}/5</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.csat.stats.totalResponses', 'Total Responses')}</p>
                <p className="text-2xl font-bold">{stats?.total_surveys || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.csat.stats.responseRate', 'Response Rate')}</p>
                <p className="text-2xl font-bold">{stats?.response_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ThumbsUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.csat.stats.satisfied', 'Satisfied')}</p>
                <p className="text-2xl font-bold">{stats?.promoters || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.csat.scoreDistribution', 'CSAT Score Distribution')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : !summary || summary.total === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('platform.customerSupport.csat.noResponses', 'No CSAT responses yet')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-5 gap-4">
                {(() => {
                  const counts = [1, 2, 3, 4, 5].map((r) => summary.distribution?.[String(r)] || 0);
                  const maxCount = Math.max(...counts, 1);
                  return [1, 2, 3, 4, 5].map((rating, i) => {
                    const count = counts[i];
                    const heightPct = Math.round((count / maxCount) * 100);
                    return (
                      <div key={rating} className="text-center">
                        <div className="text-2xl mb-2">
                          {rating === 1 ? '😞' : rating === 2 ? '😕' : rating === 3 ? '😐' : rating === 4 ? '🙂' : '😍'}
                        </div>
                        <div className="text-sm font-medium">{rating} {t('platform.customerSupport.csat.star', 'Star')}</div>
                        <div className="h-24 bg-gray-100 rounded-lg relative mt-2" title={`${count}`}>
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-blue-500 rounded-lg transition-all"
                            style={{ height: `${heightPct}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-600 mt-2">{count}</div>
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="grid grid-cols-3 gap-4 mt-6 text-center">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{summary.average_score?.toFixed(1) || '0'}/5</p>
                  <p className="text-xs text-gray-600">{t('platform.customerSupport.csat.averageScore', 'Average Score')}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{summary.satisfaction_rate || 0}%</p>
                  <p className="text-xs text-gray-600">{t('platform.customerSupport.csat.satisfactionRate', 'Satisfaction Rate')}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{summary.total || 0}</p>
                  <p className="text-xs text-gray-600">{t('platform.customerSupport.csat.totalResponses', 'Total Responses')}</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.csat.recentFeedback', 'Recent Feedback')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {responses.slice(0, 5).map((response, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 border rounded-lg">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${star <= response.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{response.feedback || t('platform.customerSupport.csat.noComment', 'No comment provided')}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Ticket #{response.ticket_number} • {new Date(response.responded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CSAT Configuration Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('platform.customerSupport.csat.dialogs.config.title', 'Configure CSAT Surveys')}</DialogTitle>
            <DialogDescription>
              {t('platform.customerSupport.csat.dialogs.config.description', 'Set up automatic CSAT surveys after ticket resolution')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">{t('platform.customerSupport.csat.dialogs.config.enableSurveys', 'Enable CSAT Surveys')}</h4>
                <p className="text-sm text-gray-500">{t('platform.customerSupport.csat.dialogs.config.enableSurveysDesc', 'Send surveys after tickets are resolved')}</p>
              </div>
              <input
                type="checkbox"
                checked={configForm.is_enabled}
                onChange={(e) => setConfigForm({ ...configForm, is_enabled: e.target.checked })}
                className="h-5 w-5"
              />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">{t('platform.customerSupport.csat.dialogs.config.autoSend', 'Auto-Send Surveys')}</h4>
                <p className="text-sm text-gray-500">{t('platform.customerSupport.csat.dialogs.config.autoSendDesc', 'Automatically send after ticket closure')}</p>
              </div>
              <input
                type="checkbox"
                checked={configForm.auto_send}
                onChange={(e) => setConfigForm({ ...configForm, auto_send: e.target.checked })}
                className="h-5 w-5"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.csat.dialogs.config.delayLabel', 'Delay Before Sending (hours)')}</Label>
              <Input
                type="number"
                min={1}
                max={168}
                value={configForm.delay_hours}
                onChange={(e) => setConfigForm({ ...configForm, delay_hours: parseInt(e.target.value) })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">{t('platform.customerSupport.csat.dialogs.config.delayHint', 'Wait this many hours after ticket closure before sending survey')}</p>
            </div>
            <div>
              <Label>{t('platform.customerSupport.csat.dialogs.config.thankYouLabel', 'Thank You Message')}</Label>
              <Textarea
                value={configForm.thank_you_message}
                onChange={(e) => setConfigForm({ ...configForm, thank_you_message: e.target.value })}
                placeholder={t('platform.customerSupport.csat.dialogs.config.thankYouPlaceholder', 'Message shown after survey submission')}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
            <Button onClick={handleSaveConfig}>{t('platform.customerSupport.common.saveConfiguration', 'Save Configuration')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// KNOWLEDGE BASE TAB
// ============================================================================

function KnowledgeBaseTab() {
  const { t } = useAppTranslation();
  const [articles, setArticles] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newArticle, setNewArticle] = useState({ title: '', body: '', category_id: '', status: 'draft' });
  const [viewArticle, setViewArticle] = useState<any | null>(null);
  const [editArticle, setEditArticle] = useState<any | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          // include_unpublished so admins can review/publish drafts
          apiClient.get('/api/platform/v1/support/kb/articles?include_unpublished=true&limit=100'),
          apiClient.get('/api/platform/v1/support/kb/categories'),
        ]);
        setArticles(articlesRes.data.articles || []);
        setCategories(categoriesRes.data.categories || []);
      } catch (err) {
        console.error('Error fetching KB data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const reloadArticles = async () => {
    const res = await apiClient.get('/api/platform/v1/support/kb/articles?include_unpublished=true&limit=100');
    setArticles(res.data.articles || []);
  };

  const handleCreateArticle = async () => {
    try {
      await apiClient.post('/api/platform/v1/support/kb/articles', {
        title: newArticle.title,
        body: newArticle.body,
        category_id: newArticle.category_id,
        status: newArticle.status,
      });
      setShowCreateModal(false);
      setNewArticle({ title: '', body: '', category_id: '', status: 'draft' });
      await reloadArticles();
    } catch (err: any) {
      alert('Failed to create article: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePublishToggle = async (article: any) => {
    const action = article.status === 'published' ? 'unpublish' : 'publish';
    try {
      await apiClient.post(`/api/platform/v1/support/kb/articles/${article.id}/${action}`);
      await reloadArticles();
    } catch (err: any) {
      alert(`Failed to ${action} article: ` + (err.response?.data?.detail || err.message));
    }
  };

  const handleView = async (article: any) => {
    try {
      const res = await apiClient.get(`/api/platform/v1/support/kb/articles/${article.id}`);
      setViewArticle(res.data.article || article);
    } catch (err: any) {
      alert('Failed to load article: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = async (article: any) => {
    try {
      const res = await apiClient.get(`/api/platform/v1/support/kb/articles/${article.id}`);
      setEditArticle(res.data.article || article);
    } catch (err: any) {
      alert('Failed to load article: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveEdit = async () => {
    if (!editArticle) return;
    setSavingEdit(true);
    try {
      await apiClient.put(`/api/platform/v1/support/kb/articles/${editArticle.id}`, {
        title: editArticle.title,
        excerpt: editArticle.excerpt,
        body: editArticle.body,
      });
      setEditArticle(null);
      await reloadArticles();
    } catch (err: any) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingEdit(false);
    }
  };

  const filteredArticles = articles.filter(a =>
    a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.body?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('platform.customerSupport.knowledgeBase.searchPlaceholder', 'Search articles...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('platform.customerSupport.knowledgeBase.newArticle', 'New Article')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Book className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.knowledgeBase.stats.totalArticles', 'Total Articles')}</p>
                <p className="text-2xl font-bold">{articles.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.knowledgeBase.stats.published', 'Published')}</p>
                <p className="text-2xl font-bold">{articles.filter(a => a.status === 'published').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Edit className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.knowledgeBase.stats.drafts', 'Drafts')}</p>
                <p className="text-2xl font-bold">{articles.filter(a => a.status === 'draft').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.knowledgeBase.articles', 'Articles')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Book className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('platform.customerSupport.knowledgeBase.noArticles', 'No articles found')}</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                {t('platform.customerSupport.knowledgeBase.createFirstArticle', 'Create First Article')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredArticles.map((article) => (
                <div key={article.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{article.title}</h4>
                      <Badge variant={article.status === 'published' ? 'default' : 'secondary'}>
                        {article.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{article.excerpt || article.body?.substring(0, 100)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={article.status === 'published' ? 'ghost' : 'default'}
                      size="sm"
                      onClick={() => handlePublishToggle(article)}
                    >
                      {article.status === 'published' ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleView(article)}><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(article)}><Edit className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('platform.customerSupport.knowledgeBase.dialogs.create.title', 'Create Knowledge Base Article')}</DialogTitle>
            <DialogDescription>{t('platform.customerSupport.knowledgeBase.dialogs.create.description', 'Write and format your article using the rich text editor below')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('platform.customerSupport.knowledgeBase.dialogs.create.titleLabel', 'Title')}</Label>
              <Input
                value={newArticle.title}
                onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                placeholder={t('platform.customerSupport.knowledgeBase.dialogs.create.titlePlaceholder', 'Article title')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.knowledgeBase.dialogs.create.categoryLabel', 'Category')}</Label>
              <Select value={newArticle.category_id} onValueChange={(v) => setNewArticle({ ...newArticle, category_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={t('platform.customerSupport.knowledgeBase.dialogs.create.selectCategory', 'Select category')} /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  {categories.length === 0 ? (
                    <SelectItem value="uncategorized">{t('platform.customerSupport.knowledgeBase.uncategorized', 'Uncategorized')}</SelectItem>
                  ) : (
                    categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('platform.customerSupport.knowledgeBase.dialogs.create.contentLabel', 'Content')}</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={newArticle.body}
                  onChange={(body) => setNewArticle({ ...newArticle, body })}
                  placeholder={t('platform.customerSupport.knowledgeBase.dialogs.create.contentPlaceholder', 'Write your article content using Markdown formatting...')}
                  minHeight="250px"
                />
              </div>
            </div>
            <div>
              <Label>{t('platform.customerSupport.knowledgeBase.dialogs.create.statusLabel', 'Status')}</Label>
              <Select value={newArticle.status} onValueChange={(v) => setNewArticle({ ...newArticle, status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="draft">{t('platform.customerSupport.knowledgeBase.draft', 'Draft')}</SelectItem>
                  <SelectItem value="published">{t('platform.customerSupport.knowledgeBase.published', 'Published')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
            <Button onClick={handleCreateArticle}>{t('platform.customerSupport.knowledgeBase.dialogs.create.createButton', 'Create Article')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View article */}
      <Dialog open={!!viewArticle} onOpenChange={(o) => !o && setViewArticle(null)}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewArticle?.title}
              {viewArticle && (
                <Badge variant={viewArticle.status === 'published' ? 'default' : 'secondary'}>
                  {viewArticle.status}
                </Badge>
              )}
            </DialogTitle>
            {viewArticle?.excerpt && <DialogDescription>{viewArticle.excerpt}</DialogDescription>}
          </DialogHeader>
          <div className="py-4 whitespace-pre-wrap text-sm text-gray-800">{viewArticle?.body}</div>
          <DialogFooter>
            {viewArticle && (
              <Button variant="outline" onClick={() => { const a = viewArticle; setViewArticle(null); handleEdit(a); }}>
                Edit
              </Button>
            )}
            <Button onClick={() => setViewArticle(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit article */}
      <Dialog open={!!editArticle} onOpenChange={(o) => !o && setEditArticle(null)}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Article</DialogTitle>
            <DialogDescription>Update the article title, summary, and content.</DialogDescription>
          </DialogHeader>
          {editArticle && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editArticle.title || ''}
                  onChange={(e) => setEditArticle({ ...editArticle, title: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Summary</Label>
                <Input
                  value={editArticle.excerpt || ''}
                  onChange={(e) => setEditArticle({ ...editArticle, excerpt: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Content</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={editArticle.body || ''}
                    onChange={(body) => setEditArticle({ ...editArticle, body })}
                    placeholder={t('platform.customerSupport.knowledgeBase.dialogs.create.contentPlaceholder', 'Write your article content using Markdown formatting...')}
                    minHeight="250px"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditArticle(null)} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// SURVEYS TAB
// ============================================================================

function SurveysTab() {
  const { t } = useAppTranslation();
  const [surveys, setSurveys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSurvey, setNewSurvey] = useState({ name: '', description: '', type: 'csat' });
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sendSurvey, setSendSurvey] = useState<any | null>(null);
  const [sendEmails, setSendEmails] = useState('');
  const [sendIntro, setSendIntro] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ sent: number; total: number; link: string } | null>(null);

  const loadSurveys = async () => {
    // Real surveys list. skipAuthLogout: a 401 here (e.g. cross-product
    // token) must NOT log the user out — it previously crashed the page.
    const res = await apiClient.get('/api/surveys', { skipAuthLogout: true } as any);
    const data: any = res.data;
    setSurveys(Array.isArray(data) ? data : data?.surveys || data?.templates || []);
  };

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        await loadSurveys();
      } catch (err) {
        console.error('Error fetching surveys:', err);
        setSurveys([]); // honest empty state instead of fake data
      } finally {
        setLoading(false);
      }
    };
    fetchSurveys();
  }, []);

  const handleCreateSurvey = async () => {
    setCreating(true);
    try {
      await apiClient.post('/api/surveys', {
        name: newSurvey.name,
        survey_type: newSurvey.type,
        description: newSurvey.description,
      });
      setShowCreateModal(false);
      setNewSurvey({ name: '', description: '', type: 'csat' });
      await loadSurveys();
    } catch (err: any) {
      alert('Failed to create survey: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async (survey: any) => {
    try {
      const res = await apiClient.get(`/api/surveys/${survey.id}/share-link`);
      const link = res.data?.link;
      if (link) {
        await navigator.clipboard.writeText(link);
        setCopiedId(survey.id);
        setTimeout(() => setCopiedId((cur) => (cur === survey.id ? null : cur)), 2500);
      }
    } catch (err: any) {
      alert('Failed to get share link: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openSendDialog = (survey: any) => {
    setSendSurvey(survey);
    setSendEmails('');
    setSendIntro('');
    setSendResult(null);
  };

  const handleSend = async () => {
    if (!sendSurvey) return;
    const emails = sendEmails
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      alert('Enter at least one email address');
      return;
    }
    setSending(true);
    try {
      const res = await apiClient.post(`/api/surveys/${sendSurvey.id}/send`, {
        emails,
        ...(sendIntro.trim() ? { intro: sendIntro.trim() } : {}),
      });
      setSendResult({
        sent: res.data?.sent ?? 0,
        total: res.data?.total ?? emails.length,
        link: res.data?.link ?? '',
      });
      await loadSurveys();
    } catch (err: any) {
      alert('Failed to send survey: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">{t('platform.customerSupport.surveys.title', 'Survey Management')}</h2>
          <p className="text-sm text-gray-500">{t('platform.customerSupport.surveys.description', 'Create and manage NPS, CSAT, and custom surveys')}</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('platform.customerSupport.surveys.createSurvey', 'Create Survey')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <p className="text-sm opacity-90">{t('platform.customerSupport.surveys.stats.totalSurveys', 'Total Surveys')}</p>
            <p className="text-3xl font-bold">{surveys.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <p className="text-sm opacity-90">{t('platform.customerSupport.surveys.stats.activeSurveys', 'Active Surveys')}</p>
            <p className="text-3xl font-bold">{surveys.filter(s => s.status === 'active').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <p className="text-sm opacity-90">{t('platform.customerSupport.surveys.stats.totalResponses', 'Total Responses')}</p>
            <p className="text-3xl font-bold">{surveys.reduce((sum, s) => sum + (s.response_count || 0), 0)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.surveys.yourSurveys', 'Your Surveys')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : surveys.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileQuestion className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('platform.customerSupport.surveys.noSurveys', 'No surveys created yet')}</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                {t('platform.customerSupport.surveys.createFirstSurvey', 'Create Your First Survey')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {surveys.map((survey) => (
                <div key={survey.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{survey.name}</h4>
                      <Badge variant={survey.status === 'active' ? 'default' : 'secondary'}>{survey.status}</Badge>
                      <Badge variant="outline">{survey.type?.toUpperCase()}</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{survey.response_count || 0} {t('platform.customerSupport.common.responses', 'responses')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleCopyLink(survey)}>
                      <ExternalLink className="h-4 w-4 mr-1" />
                      {copiedId === survey.id
                        ? t('platform.customerSupport.surveys.copied', 'Copied!')
                        : t('platform.customerSupport.surveys.copyLink', 'Copy link')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openSendDialog(survey)}>
                      <Send className="h-4 w-4 mr-1" /> {t('platform.customerSupport.surveys.send', 'Send')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('platform.customerSupport.surveys.dialogs.create.title', 'Create New Survey')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('platform.customerSupport.surveys.dialogs.create.nameLabel', 'Survey Name')}</Label>
              <Input
                value={newSurvey.name}
                onChange={(e) => setNewSurvey({ ...newSurvey, name: e.target.value })}
                placeholder={t('platform.customerSupport.surveys.dialogs.create.namePlaceholder', 'e.g., Customer Satisfaction Survey')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.surveys.dialogs.create.typeLabel', 'Survey Type')}</Label>
              <Select value={newSurvey.type} onValueChange={(v) => setNewSurvey({ ...newSurvey, type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csat">{t('platform.customerSupport.surveys.types.csat', 'CSAT (Customer Satisfaction)')}</SelectItem>
                  <SelectItem value="nps">{t('platform.customerSupport.surveys.types.nps', 'NPS (Net Promoter Score)')}</SelectItem>
                  <SelectItem value="pmf">{t('platform.customerSupport.surveys.types.pmf', 'PMF (Product-Market Fit)')}</SelectItem>
                  <SelectItem value="onboarding">{t('platform.customerSupport.surveys.types.onboarding', 'Onboarding')}</SelectItem>
                  <SelectItem value="exit">{t('platform.customerSupport.surveys.types.exit', 'Exit')}</SelectItem>
                  <SelectItem value="custom">{t('platform.customerSupport.surveys.types.custom', 'Custom Survey')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('platform.customerSupport.surveys.dialogs.create.descriptionLabel', 'Description')}</Label>
              <Textarea
                value={newSurvey.description}
                onChange={(e) => setNewSurvey({ ...newSurvey, description: e.target.value })}
                placeholder={t('platform.customerSupport.surveys.dialogs.create.descriptionPlaceholder', 'What is this survey for?')}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={creating}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
            <Button onClick={handleCreateSurvey} disabled={creating || !newSurvey.name.trim()}>
              {creating ? t('platform.customerSupport.common.saving', 'Saving…') : t('platform.customerSupport.surveys.dialogs.create.createButton', 'Create Survey')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send survey dialog */}
      <Dialog open={!!sendSurvey} onOpenChange={(o) => !o && setSendSurvey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('platform.customerSupport.surveys.dialogs.send.title', 'Send Survey')}</DialogTitle>
            <DialogDescription>
              {sendSurvey?.name}
            </DialogDescription>
          </DialogHeader>
          {sendResult ? (
            <div className="py-4 space-y-3">
              <p className="text-sm text-gray-800">
                {t('platform.customerSupport.surveys.dialogs.send.sentSummary', 'Sent')} {sendResult.sent}/{sendResult.total}
              </p>
              {sendResult.link && (
                <div className="text-xs text-gray-500 break-all">
                  {t('platform.customerSupport.surveys.dialogs.send.link', 'Link')}: {sendResult.link}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('platform.customerSupport.surveys.dialogs.send.emailsLabel', 'Recipient emails (comma-separated)')}</Label>
                <Textarea
                  value={sendEmails}
                  onChange={(e) => setSendEmails(e.target.value)}
                  placeholder="alice@example.com, bob@example.com"
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('platform.customerSupport.surveys.dialogs.send.introLabel', 'Intro message (optional)')}</Label>
                <Textarea
                  value={sendIntro}
                  onChange={(e) => setSendIntro(e.target.value)}
                  placeholder={t('platform.customerSupport.surveys.dialogs.send.introPlaceholder', 'A short note to include in the email')}
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {sendResult ? (
              <Button onClick={() => setSendSurvey(null)}>{t('platform.customerSupport.common.close', 'Close')}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSendSurvey(null)} disabled={sending}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
                <Button onClick={handleSend} disabled={sending || !sendEmails.trim()}>
                  <Send className="h-4 w-4 mr-1" />
                  {sending ? t('platform.customerSupport.surveys.dialogs.send.sending', 'Sending…') : t('platform.customerSupport.surveys.send', 'Send')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// FEEDBACK TAB
// ============================================================================

interface FeedbackItem {
  id: string;
  type: string;
  message: string;
  email?: string | null;
  user_name?: string | null;
  page?: string | null;
  url?: string | null;
  status?: string | null;
  priority?: string | null;
  created_at?: string | null;
}

interface NpsStats {
  total: number;
  promoters: number;
  passives: number;
  detractors: number;
  avg_score: number;
  nps: number;
}

const FEEDBACK_TYPE_STYLES: Record<string, string> = {
  bug: 'bg-red-100 text-red-700 border-red-200',
  feature: 'bg-blue-100 text-blue-700 border-blue-200',
  improvement: 'bg-amber-100 text-amber-700 border-amber-200',
  compliment: 'bg-green-100 text-green-700 border-green-200',
  other: 'bg-gray-100 text-gray-700 border-gray-200',
};

function feedbackTypeClass(type?: string | null): string {
  return FEEDBACK_TYPE_STYLES[(type || 'other').toLowerCase()] || FEEDBACK_TYPE_STYLES.other;
}

function formatFeedbackDate(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function FeedbackTab() {
  const { t } = useAppTranslation();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [nps, setNps] = useState<NpsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const feedbackUrl =
          typeFilter && typeFilter !== 'all'
            ? `/api/customer-support/feedback?type=${encodeURIComponent(typeFilter)}`
            : '/api/customer-support/feedback';
        const [feedbackRes, npsRes] = await Promise.all([
          apiClient.get(feedbackUrl),
          apiClient.get('/api/customer-support/feedback/nps-stats'),
        ]);
        if (cancelled) return;
        setFeedback(Array.isArray(feedbackRes.data?.feedback) ? feedbackRes.data.feedback : []);
        const npsData = npsRes.data || {};
        setNps({
          total: Number(npsData.total) || 0,
          promoters: Number(npsData.promoters) || 0,
          passives: Number(npsData.passives) || 0,
          detractors: Number(npsData.detractors) || 0,
          avg_score: Number(npsData.avg_score) || 0,
          nps: Number(npsData.nps) || 0,
        });
      } catch (err: any) {
        if (cancelled) return;
        console.error('Error fetching feedback data:', err);
        setFeedback([]);
        setNps(null);
        setError(
          t(
            'platform.customerSupport.feedback.loadError',
            'Could not load feedback right now. Please try again.',
          ),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [typeFilter, t]);

  const hasNps = nps && nps.total > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            {t('platform.customerSupport.feedback.title', 'Product Feedback')}
          </h2>
          <p className="text-sm text-gray-500">
            {t(
              'platform.customerSupport.feedback.description',
              'Feedback submitted from the in-app widget, plus NPS sentiment.',
            )}
          </p>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('platform.customerSupport.feedback.filterAll', 'All types')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('platform.customerSupport.feedback.filterAll', 'All types')}</SelectItem>
            <SelectItem value="bug">{t('platform.customerSupport.feedback.type.bug', 'Bug')}</SelectItem>
            <SelectItem value="feature">{t('platform.customerSupport.feedback.type.feature', 'Feature')}</SelectItem>
            <SelectItem value="improvement">{t('platform.customerSupport.feedback.type.improvement', 'Improvement')}</SelectItem>
            <SelectItem value="compliment">{t('platform.customerSupport.feedback.type.compliment', 'Compliment')}</SelectItem>
            <SelectItem value="other">{t('platform.customerSupport.feedback.type.other', 'Other')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* NPS summary */}
      {hasNps ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-gray-900">{nps!.nps}</p>
            <p className="text-sm text-gray-600">{t('platform.customerSupport.feedback.nps.score', 'NPS Score')}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-gray-900">{nps!.total}</p>
            <p className="text-sm text-gray-600">{t('platform.customerSupport.feedback.nps.total', 'Responses')}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-gray-900">{nps!.promoters}</p>
            <p className="text-sm text-gray-600">{t('platform.customerSupport.feedback.nps.promoters', 'Promoters')}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-gray-900">{nps!.passives}</p>
            <p className="text-sm text-gray-600">{t('platform.customerSupport.feedback.nps.passives', 'Passives')}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-2xl font-bold text-gray-900">{nps!.detractors}</p>
            <p className="text-sm text-gray-600">{t('platform.customerSupport.feedback.nps.detractors', 'Detractors')}</p>
          </div>
        </div>
      ) : (
        !loading && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              {t('platform.customerSupport.feedback.nps.empty', 'No NPS responses yet')}
            </p>
          </div>
        )
      )}

      {/* Feedback list */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.feedback.listTitle', 'Recent Feedback')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-gray-500">{error}</div>
          ) : feedback.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              {t('platform.customerSupport.feedback.empty', 'No feedback yet')}
            </div>
          ) : (
            <div className="space-y-3">
              {feedback.map((item) => {
                const submitter = item.user_name || item.email || t('platform.customerSupport.feedback.anonymous', 'Anonymous');
                const pageRef = item.page || item.url || '';
                return (
                  <div key={item.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={feedbackTypeClass(item.type)}>
                          {item.type || t('platform.customerSupport.feedback.type.other', 'Other')}
                        </Badge>
                        {item.status && (
                          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                            {item.status}
                          </Badge>
                        )}
                        {item.priority && (
                          <span className="text-xs text-gray-500">{item.priority}</span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-gray-900 whitespace-pre-wrap break-words">{item.message}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {submitter}
                        {pageRef ? ` • ${pageRef}` : ''}
                        {item.created_at ? ` • ${formatFeedbackDate(item.created_at)}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// FORUMS TAB
// ============================================================================

function ForumsTab() {
  const { t } = useAppTranslation();
  const [categories, setCategories] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Create category
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '', description: '', icon: '💬' });

  // Create discussion (post)
  const [showPostModal, setShowPostModal] = useState(false);
  const [creatingPost, setCreatingPost] = useState(false);
  const [newPost, setNewPost] = useState({ category_id: '', title: '', content: '' });

  // Thread view
  const [activeThread, setActiveThread] = useState<any | null>(null);
  const [threadReplies, setThreadReplies] = useState<any[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [postingReply, setPostingReply] = useState(false);

  const fetchData = async () => {
    setLoadError(null);
    try {
      const [catsRes, postsRes] = await Promise.all([
        apiClient.get('/api/customer-support/forums/categories'),
        apiClient.get('/api/customer-support/forums/posts?limit=20'),
      ]);
      setCategories(catsRes.data.categories || []);
      setPosts(postsRes.data.posts || []);
    } catch (err: any) {
      console.error('Error fetching forum data:', err);
      setCategories([]);
      setPosts([]);
      setLoadError(err.response?.data?.detail || err.message || 'Failed to load forums');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      alert('Please enter a category name');
      return;
    }
    setCreating(true);
    try {
      const slug = newCategory.slug || newCategory.name.toLowerCase().replace(/\s+/g, '-');
      await apiClient.post('/api/customer-support/forums/categories', {
        ...newCategory,
        slug,
      });
      setShowCreateModal(false);
      setNewCategory({ name: '', slug: '', description: '', icon: '💬' });
      fetchData();
    } catch (err: any) {
      alert('Failed to create category: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.category_id) {
      alert('Please choose a category');
      return;
    }
    if (newPost.title.trim().length < 5) {
      alert('Title must be at least 5 characters');
      return;
    }
    if (newPost.content.trim().length < 10) {
      alert('Discussion body must be at least 10 characters');
      return;
    }
    setCreatingPost(true);
    try {
      await apiClient.post('/api/customer-support/forums/posts', {
        category_id: newPost.category_id,
        title: newPost.title.trim(),
        content: newPost.content.trim(),
      });
      setShowPostModal(false);
      setNewPost({ category_id: '', title: '', content: '' });
      fetchData();
    } catch (err: any) {
      alert('Failed to start discussion: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreatingPost(false);
    }
  };

  const openThread = async (postId: string) => {
    setLoadingThread(true);
    setActiveThread({ id: postId });
    setThreadReplies([]);
    try {
      const res = await apiClient.get(`/api/customer-support/forums/posts/${postId}`);
      setActiveThread(res.data.post);
      setThreadReplies(res.data.replies || []);
    } catch (err: any) {
      alert('Failed to open discussion: ' + (err.response?.data?.detail || err.message));
      setActiveThread(null);
    } finally {
      setLoadingThread(false);
    }
  };

  const handleReply = async () => {
    if (!activeThread?.id || replyText.trim().length < 1) return;
    setPostingReply(true);
    try {
      await apiClient.post(`/api/customer-support/forums/posts/${activeThread.id}/replies`, {
        content: replyText.trim(),
      });
      setReplyText('');
      await openThread(activeThread.id); // reload thread with the new reply
      fetchData(); // refresh reply counts in the list
    } catch (err: any) {
      alert('Failed to post reply: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPostingReply(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">{t('platform.customerSupport.forums.title', 'Community Forums')}</h2>
          <p className="text-sm text-gray-500">{t('platform.customerSupport.forums.description', 'Customer-to-customer support and discussions')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('platform.customerSupport.forums.newCategory', 'New Category')}
          </Button>
          <Button onClick={() => setShowPostModal(true)} disabled={categories.length === 0}>
            <MessageCircle className="h-4 w-4 mr-2" />
            {t('platform.customerSupport.forums.newDiscussion', 'New Discussion')}
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {loadError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {categories.map((cat) => (
          <Card
            key={cat.id}
            className="hover:border-blue-500 cursor-pointer transition-colors"
            onClick={() => { setNewPost({ category_id: cat.id, title: '', content: '' }); setShowPostModal(true); }}
          >
            <CardContent className="pt-6">
              <div className="text-3xl mb-2">{cat.icon || '💬'}</div>
              <h3 className="font-semibold">{cat.name}</h3>
              {cat.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{cat.description}</p>}
              <p className="text-sm text-gray-500 mt-1">{cat.post_count ?? 0} {t('platform.customerSupport.forums.posts', 'posts')}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.forums.recentDiscussions', 'Recent Discussions')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('platform.customerSupport.forums.noDiscussions', 'No discussions yet')}</p>
              {categories.length > 0 && (
                <Button className="mt-4" variant="outline" onClick={() => setShowPostModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('platform.customerSupport.forums.startFirst', 'Start the first discussion')}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => openThread(post.id)}
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-600 hover:underline">
                      {post.is_pinned && <span className="mr-1">📌</span>}
                      {post.title}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {post.category_name ? `${post.category_name} • ` : ''}
                      by {post.author_name || 'Unknown'} • {new Date(post.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><MessageCircle className="h-4 w-4" /> {post.reply_count ?? 0}</span>
                    <span className="flex items-center gap-1"><Eye className="h-4 w-4" /> {post.view_count ?? 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Category dialog */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('platform.customerSupport.forums.dialogs.create.title', 'Create Forum Category')}</DialogTitle>
            <DialogDescription>{t('platform.customerSupport.forums.dialogs.create.description', 'Add a new category for community discussions')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('platform.customerSupport.forums.dialogs.create.nameLabel', 'Category Name')}</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder={t('platform.customerSupport.forums.dialogs.create.namePlaceholder', 'e.g., General Discussion')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.forums.dialogs.create.slugLabel', 'Slug (URL-friendly name)')}</Label>
              <Input
                value={newCategory.slug}
                onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value })}
                placeholder={t('platform.customerSupport.forums.dialogs.create.slugPlaceholder', 'e.g., general-discussion (auto-generated if empty)')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.forums.dialogs.create.iconLabel', 'Icon (emoji)')}</Label>
              <Input
                value={newCategory.icon}
                onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                placeholder="e.g., 💬"
                className="mt-1 w-20"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.forums.dialogs.create.descriptionLabel', 'Description')}</Label>
              <Textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                placeholder={t('platform.customerSupport.forums.dialogs.create.descriptionPlaceholder', 'What topics are discussed in this category?')}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
            <Button onClick={handleCreateCategory} disabled={creating}>
              {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('platform.customerSupport.common.creating', 'Creating...')}</> : t('platform.customerSupport.forums.dialogs.create.createButton', 'Create Category')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Discussion dialog */}
      <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('platform.customerSupport.forums.dialogs.post.title', 'Start a Discussion')}</DialogTitle>
            <DialogDescription>{t('platform.customerSupport.forums.dialogs.post.description', 'Post a question or topic for the community')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('platform.customerSupport.forums.dialogs.post.categoryLabel', 'Category')}</Label>
              <Select value={newPost.category_id} onValueChange={(v) => setNewPost({ ...newPost, category_id: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('platform.customerSupport.forums.dialogs.post.categoryPlaceholder', 'Choose a category')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.icon ? `${cat.icon} ` : ''}{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('platform.customerSupport.forums.dialogs.post.titleLabel', 'Title')}</Label>
              <Input
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                placeholder={t('platform.customerSupport.forums.dialogs.post.titlePlaceholder', 'What do you want to discuss?')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('platform.customerSupport.forums.dialogs.post.bodyLabel', 'Details')}</Label>
              <Textarea
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                placeholder={t('platform.customerSupport.forums.dialogs.post.bodyPlaceholder', 'Share the details of your question or topic...')}
                rows={6}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPostModal(false)}>{t('platform.customerSupport.common.cancel', 'Cancel')}</Button>
            <Button onClick={handleCreatePost} disabled={creatingPost}>
              {creatingPost ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('platform.customerSupport.common.posting', 'Posting...')}</> : t('platform.customerSupport.forums.dialogs.post.postButton', 'Post Discussion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thread / discussion detail dialog */}
      <Dialog open={!!activeThread} onOpenChange={(open) => { if (!open) { setActiveThread(null); setThreadReplies([]); setReplyText(''); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {loadingThread || !activeThread?.title ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">
                  {activeThread.is_pinned && <span className="mr-1">📌</span>}
                  {activeThread.title}
                </DialogTitle>
                <DialogDescription>
                  {activeThread.category_name ? `${activeThread.category_name} • ` : ''}
                  by {activeThread.author_name || 'Unknown'} • {activeThread.created_at ? new Date(activeThread.created_at).toLocaleDateString() : ''}
                  {' • '}{activeThread.view_count ?? 0} views
                </DialogDescription>
              </DialogHeader>

              <div className="py-2 whitespace-pre-wrap text-sm text-gray-800 border-b pb-4">
                {activeThread.content}
              </div>

              <div className="space-y-3 py-2">
                <p className="text-sm font-medium text-gray-600">
                  {threadReplies.length} {t('platform.customerSupport.forums.replies', 'replies')}
                </p>
                {threadReplies.map((r) => (
                  <div key={r.id} className="rounded-lg border p-3 bg-gray-50">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <span className="font-medium text-gray-700">{r.author_name || 'Unknown'}</span>
                      {r.is_staff_reply && <Badge variant="secondary" className="text-[10px]">Staff</Badge>}
                      {r.is_accepted && <Badge className="text-[10px] bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-0.5" />Accepted</Badge>}
                      <span>• {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-gray-800">{r.content}</div>
                  </div>
                ))}
              </div>

              {!activeThread.is_locked ? (
                <div className="pt-2 border-t space-y-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={t('platform.customerSupport.forums.replyPlaceholder', 'Write a reply...')}
                    rows={3}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleReply} disabled={postingReply || replyText.trim().length < 1}>
                      {postingReply ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('platform.customerSupport.common.posting', 'Posting...')}</> : <><Send className="h-4 w-4 mr-2" />{t('platform.customerSupport.forums.reply', 'Reply')}</>}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="pt-2 border-t text-sm text-gray-500 text-center py-3">
                  🔒 {t('platform.customerSupport.forums.locked', 'This discussion is locked')}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// ONBOARDING TAB — imported from ./components/OnboardingTab.tsx
// ============================================================================

/* OnboardingTab is imported from ./components/OnboardingTab.tsx — see top-level import */


// ----------------------------------------------------------------------------
// Support Agents (Trust Care org agent pool) management
// ----------------------------------------------------------------------------

type AvailabilityStatus = 'available' | 'busy' | 'away' | 'offline';

interface OrgAgent {
  id: string | number;
  name: string;
  email: string;
  role?: string;
  is_agent: boolean;
  availability?: AvailabilityStatus;
  max_concurrent_tickets?: number;
}

const AVAILABILITY_DOT: Record<AvailabilityStatus, string> = {
  available: 'bg-green-500',
  busy: 'bg-orange-500',
  away: 'bg-amber-500',
  offline: 'bg-gray-400',
};

function SupportAgentsCard() {
  const { t } = useAppTranslation();
  const [agents, setAgents] = useState<OrgAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const fetchAgents = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/customer-support/assignment/agents/all');
      setAgents(res.data?.agents ?? []);
      setError(false);
    } catch (err) {
      console.error('Error fetching support agents:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const setRowPending = (id: string, value: boolean) =>
    setPending(prev => ({ ...prev, [id]: value }));

  const handleDesignate = async (agent: OrgAgent, isAgent: boolean) => {
    const id = String(agent.id);
    setRowPending(id, true);
    // Optimistic update
    setAgents(prev => prev.map(a => (String(a.id) === id ? { ...a, is_agent: isAgent } : a)));
    try {
      await apiClient.post(`/api/customer-support/assignment/agents/${agent.id}/designate`, {
        is_agent: isAgent,
        ...(agent.max_concurrent_tickets ? { max_concurrent_tickets: agent.max_concurrent_tickets } : {}),
      });
      await fetchAgents();
    } catch (err: any) {
      console.error('Error updating agent designation:', err);
      // Revert optimistic update on failure
      setAgents(prev => prev.map(a => (String(a.id) === id ? { ...a, is_agent: !isAgent } : a)));
      alert(t('platform.customerSupport.assignment.designateError', 'Failed to update agent') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setRowPending(id, false);
    }
  };

  const handleAvailability = async (agent: OrgAgent, availability: AvailabilityStatus) => {
    const id = String(agent.id);
    const previous = agent.availability;
    setRowPending(id, true);
    setAgents(prev => prev.map(a => (String(a.id) === id ? { ...a, availability } : a)));
    try {
      await apiClient.put(`/api/customer-support/assignment/agents/${agent.id}/availability-status`, {
        availability,
      });
      await fetchAgents();
    } catch (err: any) {
      console.error('Error updating agent availability:', err);
      setAgents(prev => prev.map(a => (String(a.id) === id ? { ...a, availability: previous } : a)));
      alert(t('platform.customerSupport.assignment.availabilityError', 'Failed to update availability') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setRowPending(id, false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          {t('platform.customerSupport.assignment.agents.title', 'Support Agents')}
        </CardTitle>
        <CardDescription>
          {t('platform.customerSupport.assignment.agents.description', 'Designate users from the Trust Care organization as support agents and manage their availability')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-8 w-8 text-gray-400 mb-2" />
            <p className="text-gray-500">{t('platform.customerSupport.assignment.agents.loadError', 'Unable to load agents. Please try again.')}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => { setLoading(true); fetchAgents(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('platform.customerSupport.common.retry', 'Retry')}
            </Button>
          </div>
        ) : agents.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            {t('platform.customerSupport.assignment.agents.empty', 'No users found in the Trust Care organization')}
          </p>
        ) : (
          <div className="divide-y border rounded-lg">
            {agents.map((agent) => {
              const id = String(agent.id);
              const isPending = !!pending[id];
              const availability = (agent.availability ?? 'offline') as AvailabilityStatus;
              return (
                <div key={id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">{agent.name}</p>
                      {agent.role && (
                        <Badge variant="outline" className="capitalize">{agent.role.replace(/_/g, ' ')}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{agent.email}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {agent.is_agent && (
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${AVAILABILITY_DOT[availability]}`} />
                        <Select
                          value={availability}
                          onValueChange={(v) => handleAvailability(agent, v as AvailabilityStatus)}
                          disabled={isPending}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]">
                            <SelectItem value="available">{t('platform.customerSupport.assignment.agents.status.available', 'Available')}</SelectItem>
                            <SelectItem value="busy">{t('platform.customerSupport.assignment.agents.status.busy', 'Busy')}</SelectItem>
                            <SelectItem value="away">{t('platform.customerSupport.assignment.agents.status.away', 'Away')}</SelectItem>
                            <SelectItem value="offline">{t('platform.customerSupport.assignment.agents.status.offline', 'Offline')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        {t('platform.customerSupport.assignment.agents.designate', 'Designate as agent')}
                      </span>
                      <Switch
                        checked={agent.is_agent}
                        disabled={isPending}
                        onCheckedChange={(checked) => handleDesignate(agent, checked)}
                        aria-label={t('platform.customerSupport.assignment.agents.designate', 'Designate as agent')}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AssignmentTab() {
  const { t } = useAppTranslation();
  const { agents, loading } = useAgents();
  const [settings, setSettings] = useState({
    auto_assignment_enabled: true,
    assignment_method: 'round_robin',
    max_tickets_per_agent: 20,
    consider_workload: true,
    consider_skills: true,
  });
  const [saving, setSaving] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await apiClient.get('/api/customer-support/assignment/config');
        if (res.data?.config) {
          setSettings(prev => ({
            ...prev,
            auto_assignment_enabled: res.data.config.auto_assignment_enabled ?? true,
            assignment_method: res.data.config.default_assignment_method ?? 'round_robin',
            max_tickets_per_agent: res.data.config.max_tickets_per_agent ?? 20,
            consider_workload: res.data.config.consider_agent_workload ?? true,
            consider_skills: res.data.config.consider_agent_availability ?? true,
          }));
        }
      } catch (err) {
        console.error('Error fetching assignment settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/customer-support/assignment/config', {
        auto_assignment_enabled: settings.auto_assignment_enabled,
        default_assignment_method: settings.assignment_method,
        max_tickets_per_agent: settings.max_tickets_per_agent,
        consider_agent_workload: settings.consider_workload,
        consider_agent_availability: settings.consider_skills,
      });
      alert('Settings saved successfully');
    } catch (err: any) {
      alert('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: string) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            {t('platform.customerSupport.assignment.title', 'Ticket Assignment Settings')}
          </CardTitle>
          <CardDescription>{t('platform.customerSupport.assignment.description', 'Configure auto-assignment rules')}</CardDescription>
        </CardHeader>
        <CardContent>
          {settingsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium">{t('platform.customerSupport.assignment.autoAssignment', 'Auto-Assignment')}</h4>
                  <p className="text-sm text-gray-500">{t('platform.customerSupport.assignment.autoAssignmentDesc', 'Automatically assign tickets to available agents')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.auto_assignment_enabled}
                  onChange={() => handleToggle('auto_assignment_enabled')}
                  className="h-5 w-5 cursor-pointer"
                />
              </div>

              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium">{t('platform.customerSupport.assignment.method', 'Assignment Method')}</h4>
                    <p className="text-sm text-gray-500">{t('platform.customerSupport.assignment.methodDesc', 'How tickets are distributed to agents')}</p>
                  </div>
                </div>
                <Select
                  value={settings.assignment_method}
                  onValueChange={(v) => setSettings(prev => ({ ...prev, assignment_method: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="round_robin">{t('platform.customerSupport.assignment.methods.roundRobin', 'Round Robin')}</SelectItem>
                    <SelectItem value="load_balanced">{t('platform.customerSupport.assignment.methods.loadBalanced', 'Load Balanced')}</SelectItem>
                    <SelectItem value="skill_based">{t('platform.customerSupport.assignment.methods.skillBased', 'Skill-Based')}</SelectItem>
                    <SelectItem value="manual">{t('platform.customerSupport.assignment.methods.manual', 'Manual Only')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium">{t('platform.customerSupport.assignment.maxTickets', 'Max Tickets per Agent')}</h4>
                    <p className="text-sm text-gray-500">{t('platform.customerSupport.assignment.maxTicketsDesc', 'Maximum concurrent tickets an agent can handle')}</p>
                  </div>
                </div>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={settings.max_tickets_per_agent}
                  onChange={(e) => setSettings(prev => ({ ...prev, max_tickets_per_agent: parseInt(e.target.value) || 20 }))}
                  className="w-24"
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium">{t('platform.customerSupport.assignment.considerWorkload', 'Consider Workload')}</h4>
                  <p className="text-sm text-gray-500">{t('platform.customerSupport.assignment.considerWorkloadDesc', 'Factor in current ticket load when assigning')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.consider_workload}
                  onChange={() => handleToggle('consider_workload')}
                  className="h-5 w-5 cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium">{t('platform.customerSupport.assignment.considerSkills', 'Consider Skills')}</h4>
                  <p className="text-sm text-gray-500">{t('platform.customerSupport.assignment.considerSkillsDesc', 'Match tickets to agents based on skills/expertise')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.consider_skills}
                  onChange={() => handleToggle('consider_skills')}
                  className="h-5 w-5 cursor-pointer"
                />
              </div>

              <div className="pt-4">
                <Button onClick={handleSaveSettings} disabled={saving}>
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('platform.customerSupport.common.saving', 'Saving...')}</> : t('platform.customerSupport.common.saveSettings', 'Save Settings')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <SupportAgentsCard />

      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.assignment.agentAvailability', 'Agent Availability')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : agents.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('platform.customerSupport.assignment.noAgents', 'No agents configured')}</p>
          ) : (
            <div className="space-y-3">
              {agents.map((agent) => (
                <div key={agent.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      agent.status === 'available' ? 'bg-green-500' :
                      agent.status === 'busy' ? 'bg-yellow-500' :
                      agent.status === 'away' ? 'bg-orange-500' : 'bg-gray-500'
                    }`} />
                    <div>
                      <p className="font-medium">{agent.name}</p>
                      <p className="text-sm text-gray-500">{agent.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {agent.current_ticket_count}/{agent.max_concurrent_tickets} {t('platform.customerSupport.common.tickets', 'tickets')}
                    </p>
                    <Badge variant="outline" className="capitalize">{agent.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AnalyticsTab() {
  const { t } = useAppTranslation();
  const { stats, loading } = useDashboardStats();
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await apiClient.get('/api/customer-support/analytics/overview');
        setAnalyticsData(res.data);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setAnalyticsData(null); // honest empty state instead of fake data
      } finally {
        setAnalyticsLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const ticketTrends: any[] = analyticsData?.ticket_trends || [];
  const maxTrend = Math.max(1, ...ticketTrends.flatMap((d: any) => [d.created || 0, d.resolved || 0]));
  const categories: any[] = analyticsData?.resolution_by_category || [];
  const maxCategory = Math.max(1, ...categories.map((c: any) => c.count || 0));
  const agents: any[] = analyticsData?.agent_performance || [];

  if (loading || analyticsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.stats.totalOpen', 'Total Open')}</p>
                <p className="text-2xl font-bold">{analyticsData?.open_tickets ?? stats?.open_tickets ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.stats.resolved', 'Resolved')}</p>
                <p className="text-2xl font-bold">{analyticsData?.resolved_tickets ?? stats?.resolved_today ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.stats.avgResponse', 'Avg Response')}</p>
                <p className="text-2xl font-bold">{(analyticsData?.avg_first_response_time_hours ?? stats?.avg_response_time_hours ?? 0).toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.stats.csatScore', 'CSAT Score')}</p>
                <p className="text-2xl font-bold">{stats?.csat_score || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ticket Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {t('platform.customerSupport.analytics.ticketTrends', 'Weekly Ticket Trends')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ticketTrends.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('platform.customerSupport.analytics.noTrends', 'No ticket activity yet')}</p>
            </div>
          ) : (
          <div className="h-64 flex items-end justify-around gap-2">
            {ticketTrends.map((day: any, index: number) => (
              <div key={index} className="flex flex-col items-center gap-2 flex-1">
                <div className="flex gap-1 items-end h-48">
                  <div
                    className="w-6 bg-blue-500 rounded-t"
                    style={{ height: `${((day.created || 0) / maxTrend) * 100}%` }}
                    title={`Created: ${day.created}`}
                  />
                  <div
                    className="w-6 bg-green-500 rounded-t"
                    style={{ height: `${((day.resolved || 0) / maxTrend) * 100}%` }}
                    title={`Resolved: ${day.resolved}`}
                  />
                </div>
                <span className="text-xs text-gray-500">{day.date}</span>
              </div>
            ))}
          </div>
          )}
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              <span className="text-sm text-gray-600">{t('platform.customerSupport.analytics.created', 'Created')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded" />
              <span className="text-sm text-gray-600">{t('platform.customerSupport.analytics.resolved', 'Resolved')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Resolution by Category */}
        <Card>
          <CardHeader>
            <CardTitle>{t('platform.customerSupport.analytics.resolutionByCategory', 'Resolution by Category')}</CardTitle>
          </CardHeader>
          <CardContent>
            {categories.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {t('platform.customerSupport.analytics.noCategoryData', 'No resolved tickets to categorize yet')}
              </div>
            ) : (
            <div className="space-y-4">
              {categories.map((cat: any, index: number) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{cat.category}</span>
                      <span className="text-sm text-gray-500">{cat.count} {t('platform.customerSupport.common.tickets', 'tickets')}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${((cat.count || 0) / maxCategory) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t('platform.customerSupport.analytics.avgResolution', 'Avg resolution')}: {cat.avg_hours}h</p>
                  </div>
                </div>
              ))}
            </div>
            )}
          </CardContent>
        </Card>

        {/* Agent Performance */}
        <Card>
          <CardHeader>
            <CardTitle>{t('platform.customerSupport.analytics.agentPerformance', 'Agent Performance')}</CardTitle>
          </CardHeader>
          <CardContent>
            {agents.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {t('platform.customerSupport.analytics.noAgentData', 'No agent activity yet')}
              </div>
            ) : (
            <div className="space-y-4">
              {agents.map((agent: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium">{agent.name}</p>
                      <p className="text-sm text-gray-500">{agent.resolved} {t('platform.customerSupport.analytics.resolvedThisWeek', 'resolved this week')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">{agent.csat}% CSAT</p>
                    <p className="text-xs text-gray-500">{t('platform.customerSupport.analytics.avg', 'Avg')} {agent.avg_time}h</p>
                  </div>
                </div>
              ))}
            </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.customerSupport.analytics.performanceMetrics', 'Performance Metrics')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{stats?.first_contact_resolution || 0}%</p>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.firstContactResolution', 'First Contact Resolution')}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{(analyticsData?.avg_resolution_time_hours ?? stats?.avg_resolution_time_hours ?? 0).toFixed(1)}h</p>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.avgResolutionTime', 'Avg Resolution Time')}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">{stats?.tickets_today || 0}</p>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.ticketsCreatedToday', 'Tickets Created Today')}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-orange-600">{stats?.urgent_tickets || 0}</p>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.analytics.urgentTickets', 'Urgent Tickets')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SESSION REPLAYS TAB (Datadog Integration)
// ============================================================================

function SessionReplaysTab() {
  const { t } = useAppTranslation();
  const DD_SITE = import.meta.env.VITE_DD_SITE ?? 'datadoghq.com';
  const DD_SERVICE = import.meta.env.VITE_DD_SERVICE ?? 'weavein-frontend';
  const DD_APPLICATION_ID = import.meta.env.VITE_DD_APPLICATION_ID ?? '';

  // Construct Datadog RUM session replay URL
  const datadogSessionReplayUrl = `https://app.${DD_SITE}/rum/sessions?query=%40session.type%3Areplay%20%40application.id%3A${DD_APPLICATION_ID}&sort=desc`;
  const datadogErrorSessionsUrl = `https://app.${DD_SITE}/rum/sessions?query=%40session.type%3Areplay%20%40type%3Aerror%20%40application.id%3A${DD_APPLICATION_ID}&sort=desc`;
  const datadogRumDashboardUrl = `https://app.${DD_SITE}/rum/performance?query=%40application.id%3A${DD_APPLICATION_ID}`;

  const sessionReplayFeatures = [
    {
      title: t('platform.customerSupport.sessionReplays.viewAll', 'View All Session Replays'),
      description: t('platform.customerSupport.sessionReplays.viewAllDesc', 'Browse and search through all recorded user sessions'),
      icon: <PlayCircle className="h-5 w-5" />,
      url: datadogSessionReplayUrl,
      color: 'blue',
    },
    {
      title: t('platform.customerSupport.sessionReplays.withErrors', 'Sessions with Errors'),
      description: t('platform.customerSupport.sessionReplays.withErrorsDesc', 'Find sessions where users experienced errors or crashes'),
      icon: <AlertCircle className="h-5 w-5" />,
      url: datadogErrorSessionsUrl,
      color: 'red',
    },
    {
      title: t('platform.customerSupport.sessionReplays.perfDashboard', 'Performance Dashboard'),
      description: t('platform.customerSupport.sessionReplays.perfDashboardDesc', 'View page load times, Web Vitals, and performance metrics'),
      icon: <Activity className="h-5 w-5" />,
      url: datadogRumDashboardUrl,
      color: 'green',
    },
  ];

  const hipaaFeatures = [
    t('platform.customerSupport.sessionReplays.hipaa.formInputs', 'All form inputs automatically masked'),
    t('platform.customerSupport.sessionReplays.hipaa.phiPii', 'PHI/PII selectors hidden (SSN, DOB, medical notes)'),
    t('platform.customerSupport.sessionReplays.hipaa.emails', 'Email addresses and phone numbers sanitized'),
    t('platform.customerSupport.sessionReplays.hipaa.urls', 'Sensitive URL parameters removed'),
    t('platform.customerSupport.sessionReplays.hipaa.custom', 'Custom data-sensitive attributes respected'),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('platform.customerSupport.sessionReplays.title', 'Session Replays')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('platform.customerSupport.sessionReplays.description', 'Debug customer issues by watching their session recordings. All recordings are HIPAA-compliant with automatic PHI/PII masking.')}
          </p>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          {t('platform.customerSupport.sessionReplays.hipaaCompliant', 'HIPAA Compliant')}
        </Badge>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sessionReplayFeatures.map((feature, index) => (
          <Card key={index} className="hover:border-blue-300 transition-colors cursor-pointer">
            <a
              href={feature.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    feature.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                    feature.color === 'red' ? 'bg-red-100 text-red-600' :
                    'bg-green-100 text-green-600'
                  }`}>
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">{feature.title}</h3>
                      <ExternalLink className="h-3 w-3 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </a>
          </Card>
        ))}
      </div>

      {/* HIPAA Compliance Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
              <CheckCircle className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{t('platform.customerSupport.sessionReplays.hipaaTitle', 'HIPAA Privacy Protection')}</CardTitle>
              <CardDescription>
                {t('platform.customerSupport.sessionReplays.hipaaDescription', 'Session replays automatically mask sensitive healthcare information')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {hipaaFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                {feature}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* How to Use — structured, i18n-driven help (TRUS-117) */}
      <MarkdownHelpSection
        titleKey="howto.debugCustomerIssues.title"
        titleFallback="How to debug a customer issue"
        descriptionKey="howto.debugCustomerIssues.description"
        descriptionFallback="Use session replays to see exactly what a customer experienced, then resolve their ticket faster."
        steps={[
          {
            titleKey: 'howto.debugCustomerIssues.steps.getUser.title',
            titleFallback: "Get the customer's email or ID",
            bodyKey: 'howto.debugCustomerIssues.steps.getUser.body',
            bodyFallback: "Open the support ticket and copy the customer's email address from the ticket details.",
          },
          {
            titleKey: 'howto.debugCustomerIssues.steps.findSessions.title',
            titleFallback: 'Search their sessions in Datadog',
            bodyKey: 'howto.debugCustomerIssues.steps.findSessions.body',
            bodyFallback: 'Filter by the customer\'s email (for example, @usr.email:user@example.com) to find their recorded sessions.',
          },
          {
            titleKey: 'howto.debugCustomerIssues.steps.pickSession.title',
            titleFallback: 'Open the session around the issue time',
            bodyKey: 'howto.debugCustomerIssues.steps.pickSession.body',
            bodyFallback: 'Sessions are sorted by recency. Pick the one closest to when the issue was reported, favoring sessions flagged with errors.',
          },
          {
            titleKey: 'howto.debugCustomerIssues.steps.watchReplay.title',
            titleFallback: 'Watch the replay to pinpoint the problem',
            bodyKey: 'howto.debugCustomerIssues.steps.watchReplay.body',
            bodyFallback: "Follow the customer's actions step by step, including network requests and errors, to identify the root cause.",
          },
        ]}
      />

      {/* Developer Info */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-gray-200 text-gray-600">
              <Settings className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{t('platform.customerSupport.sessionReplays.config.title', 'Configuration')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('platform.customerSupport.sessionReplays.config.description', 'Session replay is enabled by default with HIPAA-compliant masking. To disable, set')}{' '}
                <code className="bg-gray-200 px-1 rounded">VITE_DD_SESSION_REPLAY_ENABLED=false</code>
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {t('platform.customerSupport.sessionReplays.config.appId', 'Application ID')}: {DD_APPLICATION_ID || t('platform.customerSupport.sessionReplays.config.notConfigured', 'Not configured')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function CustomerSupportPage() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState<SupportTab>('tickets');
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  useSubtabNavigation(
    (tab) => setActiveTab(tab as SupportTab),
    ['tickets', 'ai-chat', 'knowledge-base', 'csat', 'surveys', 'feedback', 'forums', 'onboarding', 'assignment', 'analytics', 'session-replays'] as const,
  );

  const { stats } = useDashboardStats();
  const { agents } = useAgents();
  const { categories } = useCategories();

  const tabs: { id: SupportTab; label: string; icon: React.ReactNode }[] = [
    { id: 'tickets', label: t('platform.customerSupport.tabs.tickets', 'Tickets'), icon: <MessageSquare className="h-4 w-4" /> },
    { id: 'ai-chat', label: t('platform.customerSupport.tabs.aiChatbot', 'AI Chatbot'), icon: <Bot className="h-4 w-4" /> },
    { id: 'csat', label: t('platform.customerSupport.tabs.csat', 'CSAT'), icon: <Star className="h-4 w-4" /> },
    { id: 'surveys', label: t('platform.customerSupport.tabs.surveys', 'Surveys'), icon: <FileQuestion className="h-4 w-4" /> },
    { id: 'feedback', label: t('platform.customerSupport.tabs.feedback', 'Feedback'), icon: <MessageSquare className="h-4 w-4" /> },
    { id: 'knowledge-base', label: t('platform.customerSupport.tabs.knowledgeBase', 'Knowledge Base'), icon: <Book className="h-4 w-4" /> },
    { id: 'forums', label: t('platform.customerSupport.tabs.forums', 'Forums'), icon: <MessageCircle className="h-4 w-4" /> },
    { id: 'onboarding', label: t('platform.customerSupport.tabs.onboarding', 'Onboarding'), icon: <Compass className="h-4 w-4" /> },
    { id: 'assignment', label: t('platform.customerSupport.tabs.assignment', 'Assignment'), icon: <UserCheck className="h-4 w-4" /> },
    { id: 'analytics', label: t('platform.customerSupport.tabs.analytics', 'Analytics'), icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'session-replays', label: t('platform.customerSupport.tabs.sessionReplays', 'Session Replays'), icon: <Video className="h-4 w-4" /> },
  ];

  return (
    // Triveny.ai: .triveny-platform activates the design-system overlay
    // (most notably collapses the 4 saturated dark-gradient KPI cards into
    // neutral surface cards). WeaveIn users see no change.
    <div className={`p-6 max-w-[1600px] mx-auto${config.isTriveny ? ' triveny-platform' : ''}`}>
      {/* Header — hidden for Triveny; the TvPillarBar already shows "OPERATE · Support". */}
      {!config.isTriveny && (
        <div className="mb-6">
          {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Headphones className="h-6 w-6 text-blue-600" />{t('platform.customerSupport.pageTitle', 'Customer Support')}</h1>}
          <p className="text-gray-600 mt-1">
            {t('platform.customerSupport.pageDescription', 'Manage tickets, AI chatbot, CSAT surveys, knowledge base, and customer success')}
          </p>
        </div>
      )}

      {/* v2 UX: neutral monochrome KPI cards (was 4 saturated gradient cards).
          All values come from real dashboard stats — no hardcoded/mock numbers. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Headphones className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.summary.openTickets', 'Open Tickets')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.open_tickets || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.summary.resolvedToday', 'Resolved Today')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.resolved_today || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Bot className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.summary.aiResolution', 'AI Resolution')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.ai_resolution_rate || 0}%</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Star className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.customerSupport.summary.csatScore', 'CSAT Score')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.csat_score || 0}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="flex space-x-4 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'tickets' && <TicketsTab stats={stats} onCreateTicket={() => setCreateTicketOpen(true)} />}
      {activeTab === 'ai-chat' && <AIChatTab />}
      {activeTab === 'csat' && <CSATTab />}
      {activeTab === 'surveys' && <SurveysTab />}
      {activeTab === 'feedback' && <FeedbackTab />}
      {activeTab === 'knowledge-base' && <KnowledgeBaseTab />}
      {activeTab === 'forums' && <ForumsTab />}
      {activeTab === 'onboarding' && <OnboardingTab />}
      {activeTab === 'assignment' && <AssignmentTab />}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'session-replays' && <SessionReplaysTab />}

      <CreateTicketModal
        isOpen={createTicketOpen}
        onClose={() => setCreateTicketOpen(false)}
        onSuccess={() => window.location.reload()}
        agents={agents}
        categories={categories}
      />
    </div>
  );
}

export default CustomerSupportPage;
